import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCanvasFocus, useFocusDrill, selectOnlyChanges } from '../lib/useGraphFocus'
import { OnConstructNode } from './nodes/OnConstructNode'
import { CustomEventNode } from './nodes/CustomEventNode'
import { PrintNode } from './nodes/PrintStringNode'
import { DeleteEdge }      from './nodes/DeleteEdge'
import { eventGraphState, subscribeEventGraphInsert } from '../lib/eventGraphState'
import { dropDisplacedEdges } from '../lib/graphConnectPolicy'
import { useGraphClipboard } from '../lib/useGraphClipboard'
import { createMinter } from '../lib/nodeClipboard'
import { resolveCharacterId } from '../lib/characterState'
import { availableRefsFor } from '../lib/graphAvailableRefs'
import GraphClipboardStatus from './GraphClipboardStatus'
import { GRAPH_MULTISELECT_PROPS } from '../lib/graphInteractionProps'
import { alignNodes, distributeNodes, straightenConnections, tidyGraph, arrangeShortcutFor } from '../lib/graphArrange'
import NodeContextMenu from './nodes/NodeContextMenu'
import { useStableNodeData } from '../lib/useStableNodeData'
import { GraphToolbar } from './GraphToolbar'
import { EVENT_NODE_TYPES as EVENT_TYPES } from '../lib/eventNodeTypes'
import { listNodesByType, findGraphNodes } from '../lib/graphNodeSearch'
import { requestFocus } from '../lib/graphFocus'
import BlueprintOutlineSection from './blueprint/BlueprintOutlineSection'
import PaletteGraphMatches from './blueprint/PaletteGraphMatches'
import { readGraphs } from '../lib/liveGraphs'
import { useRememberedViewport } from '../lib/useGraphViewport'
import { createGraphHistory, isUndoKeyEvent } from '../lib/graphHistory'
import { devLog } from '../lib/devLog'
import { CAMERA_RIG_TEMPLATES, instantiateRigAsGroup } from '../lib/cameraRigTemplates'
import { cameraRigState } from '../lib/cameraRigCompat'
import { GraphGroupNode } from './nodes/GraphGroupNode'
import {
  WSConnectedNode,
  WSDisconnectedNode,
  NewResponseNode,
  PrefabTriggerNode,
  ResponseCompleteNode,
  OnGestureNode,
  OnSentenceStartedNode,
  OnSentenceDataReadyNode,
  OnFirstSentenceReadyNode,
  OnSentenceCompletedNode,
  OnLastSentenceStartedNode,
  OnAllSentencesPlayedNode,
  OnTextInputNode,
  OnUserTypingNode,
  OnUserTypingCancelledNode,
  OnSessionNode,
  OnMetricsNode,
  OnSpeechStartNode,
  OnSpeechEndNode,
  OnBargeInNode,
  OnSentenceWaitingNode,
  OnServerErrorNode,
  ConvorcherSessionNode,
} from './nodes/ConvorcherEventNodes'
import { PlayFirstSentenceNode, PlayNextSentenceNode, SendTextInputNode, StopSpeechNode, PlayFaceClipNode, ListClipsNode, LogStateNode, PlayGestureNode, RestorePreviousStateNode, PlayBodyClipNode, PlayAudioNode, PlayPrefabNode, StreamFirstFrameNode, StreamLastFrameNode } from './nodes/SentenceControlNodes'
import { ParlaySTTNode } from './nodes/ParlaySTTNode'
import LoadEnvironmentNode from './nodes/LoadEnvironmentNode'
import { VoxyConfigNode } from './nodes/VoxyConfigNode'
import { constants } from '../lib/animVariables'
import { DelayNode, InputKeyNode, BranchNode, MakeLiteralNode } from './nodes/UtilityNodes'
import { SetVariableNode } from './nodes/SetVariableNode'
import { RandomNode } from './nodes/RandomNode'
import { SelectNode } from './nodes/SelectNode'
import { ConcatStringNode } from './nodes/ConcatStringNode'
import { ComparisonNode } from './nodes/ComparisonNode'
import { LogicalNode } from './nodes/LogicalNode'
import { MathNode } from './nodes/MathNode'
import { COMPARISON_ACCENT, LOGICAL_ACCENT, MATH_ACCENT } from './nodes/operatorTheme'
import { RendezvousNode, RendezvousIndexedNode, RENDEZVOUS_ACCENT } from './nodes/RendezvousNode'
import { VarGetNode } from './nodes/rule/VarGetNode'
import {
  ArrayGetNode, ArraySetNode, ArrayAddNode, ArrayRemoveNode, ArrayLengthNode, ArrayClearNode,
  MapGetNode, MapSetNode, MapRemoveNode, MapHasKeyNode, MapLengthNode,
} from './nodes/ArrayMapNodes'
import { FunctionCallNode, FunctionEntryNode, FunctionReturnNode } from './nodes/FunctionNodes'
import { MoveToLocationNode, MoveToActorNode, MoveToRandomPointNode, AbortMovementNode, OnMoveCompletedNode } from './nodes/WayfindingNodes'
import {
  GetCharacterLocationNode, GetCharacterRotationNode, GetCharacterTransformNode,
  GetCharacterForwardNode, GetCharacterRightNode, GetCharacterUpNode,
  SetCharacterLocationNode, SetCharacterRotationNode, SetCharacterTransformNode,
} from './nodes/CharacterTransformNodes'
import {
  SetCameraFovNode, SetCameraTargetNode,
  SetCameraCageNode, SetCameraSeeThroughNode, SetCameraFollowNode, GetCameraTransformNode,
  CameraAddYawNode, CameraAddPitchNode, CameraAddDollyNode, CameraPanTargetNode,
  CameraSetRigLimitsNode, CameraSetRigPoseNode, HeadTrackedCameraNode,
} from './nodes/CameraNodes'
import SetBackdropNode from './nodes/SetBackdropNode'
// The scene-colour defaults, imported rather than repeated: sceneColorDefaults
// is the one functional home the palette lock allowlists, and a second copy of
// these hexes is a second thing to update.
import { BACKDROP_SKY, BACKDROP_GROUND } from '../lib/sceneColorDefaults'
import { HEAD_TRACK_DEFAULTS } from '../lib/headTrackedCamera'
import { CAMERA_ACCENT } from '../lib/cameraTheme'
import { EVENTS_ACCENT } from './nodes/eventsTheme'
import { ACTIONS_ACCENT } from './nodes/actionsTheme'
import {
  OnPointerDownNode, OnPointerUpNode, OnPointerDragNode, OnWheelNode,
  OnKeyDownNode, OnKeyUpNode, IsButtonHeldNode, IsKeyHeldNode, GetInputAxisNode,
} from './nodes/InputNodes'
import AnchoredOverlay from './AnchoredOverlay'

// ─── Node type registry ───────────────────────────────────────────────────────
const EVENT_NODE_TYPES = {
  // Wave 2B: beginPlay merged into onConstruct. The alias keeps an
  // UNMIGRATED in-memory graph (e.g. a test injecting the old type straight
  // into eventGraphState) rendering instead of showing a broken node; every
  // hydration boundary retypes it for real (blueprintMigrations.js).
  beginPlay:              OnConstructNode,
  onConstruct:            OnConstructNode,
  customEvent:            CustomEventNode,
  print:                  PrintNode,
  printString:            PrintNode,
  playFirstSentence:         PlayFirstSentenceNode,
  playNextSentence:          PlayNextSentenceNode,
  sendTextInput:             SendTextInputNode,
  sendVoiceTextMessage:      SendTextInputNode,
  delay:                     DelayNode,
  branch:                    BranchNode,
  setVariable:               SetVariableNode,
  inputKey:                  InputKeyNode,
  onWSConnected:          WSConnectedNode,
  onWSDisconnected:       WSDisconnectedNode,
  onNewResponse:          NewResponseNode,
  onPrefabTrigger:        PrefabTriggerNode,
  onResponseComplete:     ResponseCompleteNode,
  onGesture:              OnGestureNode,
  onAnimation:            OnGestureNode, // backward-compat alias
  onSentenceStarted:      OnSentenceStartedNode,
  onSentenceDataReady:    OnSentenceDataReadyNode,
  onFirstSentenceReady:   OnFirstSentenceReadyNode,
  onSentenceCompleted:    OnSentenceCompletedNode,
  onLastSentenceStarted:  OnLastSentenceStartedNode,
  onAllSentencesPlayed:   OnAllSentencesPlayedNode,
  onTextInput:            OnTextInputNode,
  onUserTyping:           OnUserTypingNode,
  onUserTypingCancelled:  OnUserTypingCancelledNode,
  onSession:              OnSessionNode,
  onMetrics:              OnMetricsNode,
  onSpeechStart:          OnSpeechStartNode,
  onSpeechEnd:            OnSpeechEndNode,
  onBargeIn:              OnBargeInNode,
  onSentenceWaiting:      OnSentenceWaitingNode,
  onServerError:          OnServerErrorNode,
  stopSpeech:             StopSpeechNode,
  streamFirstFrame:       StreamFirstFrameNode,
  streamLastFrame:        StreamLastFrameNode,
  playFaceClip:           PlayFaceClipNode,
  listClips:              ListClipsNode,
  logState:               LogStateNode,
  playGesture:            PlayGestureNode,
  restorePreviousState:   RestorePreviousStateNode,
  playBodyClip:           PlayBodyClipNode,
  playAudio:              PlayAudioNode,
  playPrefab:             PlayPrefabNode,
  loadEnvironment:        LoadEnvironmentNode,
  setBackdrop:            SetBackdropNode,
  parlaySTT:              ParlaySTTNode,
  voxyConfig:             VoxyConfigNode,
  convorcherSession:      ConvorcherSessionNode,
  random:                 RandomNode,
  select:                 SelectNode,
  concatString:           ConcatStringNode,
  // Comparison operators (one component, keyed by type)
  equal:                  ComparisonNode,
  notEqual:               ComparisonNode,
  greater:                ComparisonNode,
  less:                   ComparisonNode,
  greaterEqual:           ComparisonNode,
  lessEqual:              ComparisonNode,
  // Logical operators (one component, keyed by type)
  and:                    LogicalNode,
  or:                     LogicalNode,
  not:                    LogicalNode,
  // Math operators (one component, keyed by type)
  add:                    MathNode,
  subtract:               MathNode,
  multiply:               MathNode,
  divide:                 MathNode,
  rendezvous:             RendezvousNode,
  rendezvousIndexed:      RendezvousIndexedNode,
  makeLiteralString:      MakeLiteralNode,
  makeLiteralFloat:       MakeLiteralNode,
  makeLiteralInt:         MakeLiteralNode,
  getVariable:            VarGetNode,
  // TArray
  arrayGet:              ArrayGetNode,
  arraySet:              ArraySetNode,
  arrayAdd:              ArrayAddNode,
  arrayRemove:           ArrayRemoveNode,
  arrayLength:           ArrayLengthNode,
  arrayClear:            ArrayClearNode,
  // TMap
  mapGet:                MapGetNode,
  mapSet:                MapSetNode,
  mapRemove:             MapRemoveNode,
  mapHasKey:             MapHasKeyNode,
  mapLength:             MapLengthNode,
  // Functions
  functionCall:          FunctionCallNode,
  functionEntry:         FunctionEntryNode,
  functionReturn:        FunctionReturnNode,
  // Wayfinding / movement (ENG-592)
  moveToLocation:        MoveToLocationNode,
  moveToActor:           MoveToActorNode,
  moveToRandomPoint:     MoveToRandomPointNode,
  abortMovement:         AbortMovementNode,
  onMoveCompleted:       OnMoveCompletedNode,
  // Character → Transform (Unreal-style getters/setters)
  getCharacterLocation:  GetCharacterLocationNode,
  getCharacterRotation:  GetCharacterRotationNode,
  getCharacterTransform: GetCharacterTransformNode,
  getCharacterForward:   GetCharacterForwardNode,
  getCharacterRight:     GetCharacterRightNode,
  getCharacterUp:        GetCharacterUpNode,
  setCharacterLocation:  SetCharacterLocationNode,
  setCharacterRotation:  SetCharacterRotationNode,
  setCharacterTransform: SetCharacterTransformNode,
  // Camera (camera-in-blueprint)
  setCameraFov:          SetCameraFovNode,
  setCameraTarget:       SetCameraTargetNode,
  setCameraCage:         SetCameraCageNode,
  setCameraSeeThrough:   SetCameraSeeThroughNode,
  setCameraFollow:       SetCameraFollowNode,
  getCameraTransform:    GetCameraTransformNode,
  // Camera rig motion — the camera IS these nodes now; there is no preset underneath.
  cameraAddYaw:          CameraAddYawNode,
  cameraAddPitch:        CameraAddPitchNode,
  cameraAddDolly:        CameraAddDollyNode,
  cameraPanTarget:       CameraPanTargetNode,
  cameraSetRigLimits:    CameraSetRigLimitsNode,
  cameraSetRigPose:      CameraSetRigPoseNode,
  headTrackedCamera:     HeadTrackedCameraNode,
  // User input
  onPointerDown:         OnPointerDownNode,
  onPointerUp:           OnPointerUpNode,
  onPointerDrag:         OnPointerDragNode,
  onWheel:               OnWheelNode,
  onKeyDown:             OnKeyDownNode,
  onKeyUp:               OnKeyUpNode,
  isButtonHeld:          IsButtonHeldNode,
  isKeyHeld:             IsKeyHeldNode,
  getInputAxis:          GetInputAxisNode,
  // Graph groups — one level of nesting (camera-in-blueprint Phase 3)
  graphGroup:            GraphGroupNode,
}
const EDGE_TYPES = { default: DeleteEdge }

// Node types that count as "events" (shown in MY BLUEPRINT left panel).
// EVENT_TYPES is imported from ../lib/eventNodeTypes — the single source
// also used by execReachability.js (ENG-981 headTrackedCamera disarm fix),
// so the two never drift apart.

// Context-menu catalog
const EVENT_PALETTE = [
  // ── Events ──
  // beginPlay is gone from the palette (Wave 2B): onConstruct is the one
  // entry event — it fires once the avatar loads, and again on every edit.
  { type: 'onConstruct',           label: 'Event On Construct',       desc: 'Fires once the avatar loads, and again after every editor edit', color: EVENTS_ACCENT, category: 'Events',  unique: true  },
  { type: 'onWSConnected',         label: 'On WS Connected',          desc: 'WebSocket connection established',                      color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onNewResponse',         label: 'On New Response',          desc: 'Standard streamed response (no prefab trigger)',        color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onPrefabTrigger',       label: 'On Prefab Trigger',        desc: 'Fires when a prefab/animation trigger arrives',         color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onResponseComplete',    label: 'On Response Complete',     desc: 'All sentence data received from server',                color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onGesture',             label: 'On Gesture',               desc: 'Sentence carries a gesture tag from the server',        color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceStarted',     label: 'On Sentence Started',      desc: 'A sentence begins playing',                             color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceDataReady',   label: 'On Sentence Data Ready',   desc: 'A sentence\'s data is ready (frames in) — per sentence, may be out of order', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onFirstSentenceReady',  label: 'On First Sentence Ready',   desc: 'The opening sentence (0) is ready to play — fires once per response', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceCompleted',   label: 'On Sentence Completed',    desc: 'A sentence finishes playing',                           color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onLastSentenceStarted', label: 'On Last Sentence Started', desc: 'Final sentence begins playing',                         color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onAllSentencesPlayed',  label: 'On All Sentences Played',  desc: 'Last sentence has finished playing',                    color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onTextInput',           label: 'On Text Input',            desc: 'User submitted text from the input bar',                color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onUserTyping',          label: 'On User Typing',           desc: 'User started typing in the input bar',                  color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onUserTypingCancelled', label: 'On User Typing Cancelled', desc: 'User cleared the input after typing',                   color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSession',             label: 'On Session',               desc: 'Session established with server',                       color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onMetrics',             label: 'On Metrics',               desc: 'Performance metrics from server',                       color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSpeechStart',        label: 'On Speech Start',          desc: 'User started speaking (VAD triggered)',                 color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSpeechEnd',          label: 'On Speech End',            desc: 'User stopped speaking (silence timeout)',               color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onBargeIn',            label: 'On Barge In',             desc: 'User spoke while avatar was talking',                   color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceWaiting',   label: 'On Sentence Waiting',    desc: 'Next sentence queued but frames not ready (delay gap)', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onServerError',        label: 'On Server Error',         desc: 'Server sent an Error message',                         color: '#ff4040', category: 'Events',  unique: true  },
  { type: 'onMoveCompleted',      label: 'On Move Completed',        desc: 'Fires when a navmesh move ends (success/failed/aborted)', color: '#ff7040', category: 'Events',  unique: false },
  { type: 'customEvent',          label: 'Custom Event',             desc: 'A named event you can fire from a state-machine transition’s Actions', color: EVENTS_ACCENT, category: 'Events',  unique: false },
  { type: 'inputKey',              label: 'Input Key',                desc: 'Fires when a bound key is pressed',                     color: '#ff7040', category: 'Events',  unique: false },
  { type: 'onPointerDown',         label: 'On Pointer Down',          desc: 'Viewport mouse/touch button pressed. Filter by button.',                  color: '#ff7040', category: 'Input', unique: false },
  { type: 'onPointerUp',           label: 'On Pointer Up',            desc: 'Viewport button released (or canceled by the browser).',                  color: '#ff7040', category: 'Input', unique: false },
  { type: 'onPointerDrag',         label: 'On Pointer Drag',          desc: 'Fires once per frame while a button is held and the pointer moved — dx/dy coalesced for that frame', color: '#ff7040', category: 'Input', unique: false },
  { type: 'onWheel',               label: 'On Wheel',                 desc: 'Fires once per frame with the wheel delta, normalized to pixels',         color: '#ff7040', category: 'Input', unique: false },
  { type: 'onKeyDown',             label: 'On Key Down',              desc: 'Bound key pressed while the viewport is hovered/focused (no auto-repeat)', color: '#ff7040', category: 'Input', unique: false },
  { type: 'onKeyUp',               label: 'On Key Up',                desc: 'Bound key released — always delivered, so a latch cannot stick',           color: '#ff7040', category: 'Input', unique: false },
  { type: 'isButtonHeld',          label: 'Is Button Held',           desc: 'True while a mouse button is down (data read — this is how you do "hold")', color: '#7ad29a', category: 'Input', unique: false },
  { type: 'isKeyHeld',             label: 'Is Key Held',              desc: 'True while the bound key is down',                                        color: '#7ad29a', category: 'Input', unique: false },
  { type: 'getInputAxis',          label: 'Get Input Axis',           desc: 'Live axis: mouseX/mouseY (this frame), wheel, pointerX/pointerY',         color: '#7ad29a', category: 'Input', unique: false },
  { type: 'cameraAddYaw',          label: 'Camera Add Yaw',           desc: 'Orbit the camera horizontally by delta × sensitivity radians',            color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraAddPitch',        label: 'Camera Add Pitch',         desc: 'Orbit the camera vertically (clamped by the rig pitch limits)',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraAddDolly',        label: 'Camera Add Dolly',         desc: 'Move toward/away from the target — multiplicative, so it feels the same at any distance', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraPanTarget',       label: 'Camera Pan Target',        desc: 'Slide the look-at along screen right/up, scaled by distance',             color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraSetRigLimits',    label: 'Camera Set Rig Limits',    desc: 'Bound dolly distance + pitch — what a preset used to hardcode',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraSetRigPose',      label: 'Camera Set Rig Pose',      desc: 'Place the camera at an absolute yaw/pitch/dist/target, optionally eased', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'headTrackedCamera',     label: 'Head-Tracked Camera',      desc: 'Webcam head tracking adds a parallax offset on top of the camera (MediaPipe, in-browser). Off by default.', color: CAMERA_ACCENT, category: 'Camera', unique: true },
  // "Presets", as graphs. These insert a cluster of ordinary, editable nodes — the whole
  // replacement for the deleted setCameraPreset enum. `rig` keys into CAMERA_RIG_TEMPLATES.
  { type: 'rigOrbit',              label: '＋ Orbit Camera Rig',       desc: 'Insert the orbit preset as a graph group: left-drag orbits, right-drag pans, middle-drag / wheel dollies. Double-click to open and edit.', color: CAMERA_ACCENT, category: 'Camera Rigs', unique: false, rig: 'orbit' },
  { type: 'rigThirdPerson',        label: '＋ Third-Person Camera Rig', desc: 'Insert the over-the-shoulder preset as a graph group: tight distance band, no pan, stays above the floor.', color: CAMERA_ACCENT, category: 'Camera Rigs', unique: false, rig: 'thirdPerson' },
  // ── Groups (camera-in-blueprint Phase 3) ──
  { type: 'graphGroup',            label: '＋ Graph Group',            desc: 'Sub-graph container — double-click to open, then add nodes inside (events included). One level of nesting only.', color: '#4fc0c8', category: 'Groups', unique: false },
  // ── Actions ──
  { type: 'playFirstSentence',     label: 'Play First Sentence',      desc: 'Start playing sentence 0 from the queue',               color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playNextSentence',      label: 'Play Next Sentence',       desc: 'Advance to the next queued sentence',                   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'sendTextInput',         label: 'Send Text Input',          desc: 'Send text to the AI and trigger a response',            color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'stopSpeech',            label: 'Stop Speech',              desc: 'Stop audio and clear sentence queue',                   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'streamFirstFrame',      label: 'Stream First Frame',       desc: 'Publish a sentence\'s first frame as a static face pose (First Frame Pose node)', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'streamLastFrame',       label: 'Stream Last Frame',        desc: 'Publish a sentence\'s last frame as a static face pose (Last Frame Pose node)',   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playBodyClip',          label: 'Play Body Clip',           desc: 'Play a body clip once and wait for it to finish',        color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playAudio',             label: 'Play Audio',               desc: 'Play a content-browser audio clip one-shot (pick from the dropdown, or a custom URL)',         color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playPrefab',            label: 'Play Prefab',              desc: 'Play a custom prefab: body + face + audio in sync',      color: '#c080ff', category: 'Actions', unique: false },
  { type: 'loadEnvironment',       label: 'Load Environment',         desc: 'Load one of the org gsplat environments — wire from On Construct for the blueprint default, or from a key press', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'setBackdrop',           label: 'Set Backdrop',             desc: 'Set the viewport sky color and ground plane. Part of the rendered frame, so it shows in recordings. Settings ▸ Scene writes one of these when you save a look to the character.', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'moveToLocation',        label: 'Move To Location',         desc: 'Walk the avatar to a world XYZ point on the navmesh',    color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'moveToActor',           label: 'Move To Actor',            desc: 'Walk to a named scene object / marker',                 color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'moveToRandomPoint',     label: 'Move To Random Point',     desc: 'Walk to a random reachable point within a radius',      color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'abortMovement',         label: 'Abort Movement',           desc: 'Stop any active move (UE StopMovement)',                color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'getCharacterLocation',  label: 'Get Character Location',   desc: 'Avatar world position as a vector [x,y,z] (UE GetActorLocation)',        color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterRotation',  label: 'Get Character Rotation',   desc: 'Avatar rotation as Euler degrees [x,y,z] (UE GetActorRotation)',         color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterTransform', label: 'Get Character Transform',  desc: 'Avatar transform {location,rotation,scale} (UE GetActorTransform)',      color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterForward',   label: 'Get Character Forward',    desc: 'Avatar forward unit vector (UE GetActorForwardVector)',                  color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterRight',     label: 'Get Character Right',      desc: 'Avatar right unit vector (UE GetActorRightVector)',                      color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterUp',        label: 'Get Character Up',         desc: 'Avatar up unit vector (UE GetActorUpVector)',                            color: '#7ad29a', category: 'Character', unique: false },
  { type: 'setCharacterLocation',  label: 'Set Character Location',   desc: 'Teleport the avatar to a world position (UE SetActorLocation)',          color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCharacterRotation',  label: 'Set Character Rotation',   desc: 'Set the avatar rotation in Euler degrees (UE SetActorRotation)',         color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCharacterTransform', label: 'Set Character Transform',  desc: 'Apply a full transform to the avatar (UE SetActorTransform)',            color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCameraFov',          label: 'Set Camera FOV',           desc: 'Set the camera field of view in degrees',                               color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraTarget',       label: 'Set Camera Target',        desc: 'Point the camera at a vector or the avatar head',                        color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraCage',         label: 'Set Camera Cage',          desc: 'Confine the camera to an AABB around the avatar (no dolly through walls)', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraSeeThrough',   label: 'Set Camera See-Through',   desc: 'Fade environment splats between the camera and the avatar head (manual or auto)', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraFollow',       label: 'Set Camera Follow',        desc: 'Continuously frame the avatar as it moves (offset + damping)',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'getCameraTransform',    label: 'Get Camera Transform',     desc: 'Live camera { location, rotation, target, fov }',                        color: '#7ad29a', category: 'Camera', unique: false },
  { type: 'parlaySTT',             label: 'Parlay STT',               desc: 'Transcribe the last utterance via Parlay — wire out-transcript into SendTextInput', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'voxyConfig',            label: 'Voxy Config',              desc: 'Initialize mic + VAD on first fire, or update thresholds live. Wire from beginPlay to start the mic.', color: ACTIONS_ACCENT, category: 'Actions', unique: true },
  { type: 'convorcherSession',     label: 'Convorcher Session',       desc: 'Declare the convorcher session (character + features) from the graph and expose session_id. Wire from beginPlay. Lets a blueprint converse as a persona distinct from the rendered character.', color: '#ff7040', category: 'Actions', unique: true },
  { type: 'setVariable',           label: 'Set Variable',             desc: 'Set an animation variable at runtime',                  color: '#40b8d0', category: 'Actions', unique: false },
  { type: 'branch',                label: 'Branch',                   desc: 'Fork exec on a bool condition (True / False)',           color: '#a0a0d0', category: 'Actions', unique: false },
  { type: 'rendezvous',            label: 'Rendezvous (AND-join)',    desc: 'Fires only once BOTH inputs A and B have arrived (auto-resets)', color: RENDEZVOUS_ACCENT, category: 'Actions', unique: false },
  { type: 'rendezvousIndexed',     label: 'Indexed Rendezvous',       desc: 'AND-join over an index: fires when A and B arrive with the same index (either order)', color: RENDEZVOUS_ACCENT, category: 'Actions', unique: false },
  { type: 'delay',                 label: 'Delay',                    desc: 'Wait N seconds then continue exec',                     color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'print',                 label: 'Print',                    desc: 'Print to viewport and console (auto-formats objects)',   color: '#6090d0', category: 'Actions', unique: false },
  // ── Math ──
  { type: 'random',                label: 'Random',                   desc: 'Generate a random float or int value',                  color: '#c080e0', category: 'Math',    unique: false },
  { type: 'select',                label: 'Select',                   desc: 'Select one of N values by index',                       color: '#e09030', category: 'Math',    unique: false },
  // Comparison Operators — two value inputs → bool
  { type: 'equal',                 label: 'Equal (==)',               desc: 'A == B (type-aware: bool/int/float/string) → bool',     color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'notEqual',              label: 'Not Equal (!=)',           desc: 'A != B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'greater',               label: 'Greater (>)',              desc: 'A > B (numeric when both numeric, else lexicographic)', color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'less',                  label: 'Less (<)',                 desc: 'A < B → bool',                                          color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'greaterEqual',          label: 'Greater or Equal (>=)',    desc: 'A >= B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'lessEqual',             label: 'Less or Equal (<=)',       desc: 'A <= B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  // Logical Operators — bool in, bool out
  { type: 'and',                   label: 'And (&&)',                 desc: 'A && B → bool (inputs coerced to bool)',                color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  { type: 'or',                    label: 'Or (||)',                  desc: 'A || B → bool',                                         color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  { type: 'not',                   label: 'Not (!)',                  desc: '!A → bool (single input)',                              color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  // Math Operators — two value inputs → number (int/float toggle per node)
  { type: 'add',                   label: 'Add (+)',                  desc: 'A + B → number (int/float toggle; operands coerce, non-numeric → 0)', color: MATH_ACCENT, category: 'Math Operators', unique: false },
  { type: 'subtract',              label: 'Subtract (−)',             desc: 'A − B → number',                                        color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'multiply',              label: 'Multiply (×)',             desc: 'A × B → number',                                        color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'divide',                label: 'Divide (÷)',               desc: 'A ÷ B → number (÷0 → 0)',                               color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'concatString',          label: 'Concat String',            desc: 'Join N strings end-to-end (e.g. build a URL)',           color: '#c060e8', category: 'Math',    unique: false },
  { type: 'makeLiteralString',     label: 'Make Literal String',      desc: 'Constant string value to wire into data pins',          color: '#c060e8', category: 'Math',    unique: false },
  { type: 'makeLiteralFloat',      label: 'Make Literal Float',       desc: 'Constant float value to wire into data pins',           color: '#60a0d0', category: 'Math',    unique: false },
  { type: 'makeLiteralInt',        label: 'Make Literal Int',         desc: 'Constant int value to wire into data pins',             color: '#60d090', category: 'Math',    unique: false },
  { type: 'getVariable',           label: 'Get Variable',             desc: 'Read an animation variable value as a data pin',        color: '#d09090', category: 'Math',    unique: false },
  // ── Collections — TArray ──
  { type: 'arrayGet',    label: 'Array Get',    desc: 'Get element at index from an array variable',             color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arraySet',    label: 'Array Set',    desc: 'Set element at index in an array variable',               color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayAdd',    label: 'Array Add',    desc: 'Append element to the end of an array variable',          color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayRemove', label: 'Array Remove', desc: 'Remove element at index from an array variable',          color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayLength', label: 'Array Length', desc: 'Output the number of elements in an array variable',      color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayClear',  label: 'Array Clear',  desc: 'Remove all elements from an array variable',              color: '#30c0a0', category: 'Collections', unique: false },
  // ── Collections — TMap ──
  { type: 'mapGet',      label: 'Map Get',      desc: 'Get value by key (int or string) from a map variable',    color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapSet',      label: 'Map Set',      desc: 'Set a key→value pair in a map variable',                  color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapRemove',   label: 'Map Remove',   desc: 'Remove a key from a map variable',                        color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapHasKey',   label: 'Map Has Key',  desc: 'Output bool — true if key exists in the map variable',    color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapLength',   label: 'Map Length',   desc: 'Output the number of keys in a map variable',             color: '#d09020', category: 'Collections', unique: false },
  // ── Debug ──
  { type: 'playFaceClip',          label: 'Play Face Clip',           desc: 'Manually trigger a face clip for 5s',                   color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'listClips',             label: 'List Clips',               desc: 'Log body + face clips to console',                      color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'logState',              label: 'Log State',                desc: 'Dump state machines + variables',                       color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'playGesture',           label: 'Play Gesture',             desc: 'Trigger a face gesture by name (wired pin)',             color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'restorePreviousState',  label: 'Restore Previous State',   desc: 'ConversationalState = PreviousConversationalState',      color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  // Visible only inside function editor (filtered by isFunctionEditor flag)
  { type: 'functionReturn', label: 'Return', desc: 'End the function and return output values to the caller', color: '#9060d0', category: 'Functions', unique: false, fnOnly: true },
]

// Node types whose singleton-ness must hold across the WHOLE blueprint, not just one graph pool
// (camera-in-blueprint Phase 3 groups). voxyConfig double-inits the mic/VAD and convorcherSession
// double-declares the session if minted once at top level and once inside a group (or in two
// different groups); headTrackedCamera is the same shape — one webcam/MediaPipe pipeline, so a
// duplicate would double-configure (and double-sync) it (ENG-981). This is deliberately NOT true of Events-category unique types (e.g.
// beginPlay): a group needs its OWN beginPlay to be reachable at all (EventRuntime dispatches
// beginPlay against every group's pool independently — see graphGroup.js's collectGroupStarters),
// so duplicate beginPlay nodes across sibling groups are intentional, not a collision.
// `onConstruct` is the same shape as beginPlay for this purpose (its own dispatch is
// EventRuntime.fireEvent('onConstruct'), which walks collectGroupStarters exactly like
// 'beginPlay' does), so it is likewise NOT in this set.
const GLOBAL_UNIQUE_TYPES = new Set(['voxyConfig', 'convorcherSession', 'headTrackedCamera'])

// Which GLOBAL_UNIQUE_TYPES already exist somewhere OTHER than the pool about to be added into:
// the top-level graph, and every graphGroup's inner nodes except `excludeGroupId` (the group
// being edited, whose own nodes the caller checks locally instead).
function globalUniqueTypesInUse(topNodes, excludeGroupId = null) {
  const found = new Set()
  for (const n of topNodes) {
    if (GLOBAL_UNIQUE_TYPES.has(n.type)) found.add(n.type)
    if (n.type === 'graphGroup' && n.id !== excludeGroupId) {
      for (const inner of n.data?.innerNodes ?? []) {
        if (GLOBAL_UNIQUE_TYPES.has(inner.type)) found.add(inner.type)
      }
    }
  }
  return found
}

const TYPE_COLOR    = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8', array: '#30c0a0', map: '#d09020' }
const TYPE_DEFAULTS = { bool: false, float: 0, int: 0, string: '', array: [], map: {} }

// Persist across HMR reloads — module-level vars reset but window does not
if (!window.__evNodeId__) window.__evNodeId__ = 1
const nextEvId = () => `ev_${window.__evNodeId__++}`

const nextVarId = () => `var_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`

const INITIAL_EVENT_NODES = [
  { id: 'onConstruct_default',      type: 'onConstruct',         position: { x: 80,  y: 80  }, data: {}, deletable: false },
  { id: 'onPrefabTrigger_default',  type: 'onPrefabTrigger',     position: { x: 80,  y: 240 }, data: {} },
  { id: 'playFirst_default',        type: 'playFirstSentence',   position: { x: 380, y: 240 }, data: {} },
  { id: 'onSentCompleted_default',  type: 'onSentenceCompleted', position: { x: 80,  y: 400 }, data: {} },
  { id: 'playNext_default',         type: 'playNextSentence',    position: { x: 380, y: 400 }, data: {} },
]

const INITIAL_EVENT_EDGES = [
  { id: 'e_newResp_playFirst', source: 'onPrefabTrigger_default', sourceHandle: 'execOut', target: 'playFirst_default',       targetHandle: 'execIn', type: 'default' },
  { id: 'e_sentComp_playNext', source: 'onSentCompleted_default', sourceHandle: 'execOut', target: 'playNext_default',        targetHandle: 'execIn', type: 'default' },
]

// ─── Context menu ─────────────────────────────────────────────────────────────
function ContextMenu({ screenX, screenY, existingTypes, onAdd, onFocusExisting, onPickExistingNode, onClose, isFunctionEditor = false, isGroupEditor = false, functionDefs = [], onAddFunctionCall }) {
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({})
  const [tooltip, setTooltip] = useState(null)
  const inputRef = useRef(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  const filtered = EVENT_PALETTE.filter(item => {
    // fnOnly items only appear inside the function editor
    if (item.fnOnly && !isFunctionEditor) return false
    // Event nodes don't make sense inside functions
    if (isFunctionEditor && item.category === 'Events') return false
    // One level of nesting only: a group's own palette must not offer 'graphGroup' itself,
    // nor the camera-rig template entries (they mint a graphGroup — see cameraRigTemplates.js
    // instantiateRigAsGroup) — both would produce a nested group, which the runtime
    // (graphGroup.js) deliberately never recurses into.
    if (isGroupEditor && (item.type === 'graphGroup' || item.rig)) return false
    // Space-insensitive match (Unreal-style): "getc" / "getcharloc" finds
    // "Get Character Location". Also match the node type ("getCharacterLocation").
    const q = search.toLowerCase().replace(/\s+/g, '')
    if (!q) return true
    const hay = (item.label + ' ' + item.desc + ' ' + item.type).toLowerCase().replace(/\s+/g, '')
    return hay.includes(q)
  }).map(item =>
    // Unique node types that already exist stay LISTED, greyed, and click
    // focuses the existing node instead of adding a duplicate. Hiding them
    // entirely (the old behavior) made a node buried under another one read
    // as "this node type doesn't exist" — three people debugged the mic that
    // way before anyone found the covered Voxy Config node (2026-07-16).
    item.unique && existingTypes.has(item.type) ? { ...item, _exists: true } : item
  )

  // Inject defined functions as "Call <name>" entries under Functions category
  const fnItems = functionDefs
    .filter(fn => fn.name.toLowerCase().includes(search.toLowerCase()) || search === '')
    .map(fn => ({ type: 'functionCall', label: `Call ${fn.name}`, desc: `Call the "${fn.name}" function`, color: '#9060d0', category: 'Functions', _functionId: fn.id }))

  const allItems = [...filtered, ...fnItems]

  // Live graph snapshot for the "IN THIS GRAPH" section. Read per render, not
  // memoised: these are module singletons mutated in place, so there is nothing
  // to memo against that would not go stale.
  const graphs = readGraphs()
  const graphMatches = search.trim()
    ? findGraphNodes(graphs, search, { preferGraph: 'event', limit: 32 }).filter(m => m.graph === 'event')
    : []

  // Group by category
  const categories = []
  const byCategory = {}
  for (const item of allItems) {
    const cat = item.category ?? 'Other'
    if (!byCategory[cat]) { byCategory[cat] = []; categories.push(cat) }
    byCategory[cat].push(item)
  }

  // What Enter acts on: exactly the row the user can see at the top. An existing
  // node beats a catalog entry, matching the rendered order.
  const firstShown = graphMatches.length
    ? { _match: graphMatches[0] }
    : (categories.length ? byCategory[categories[0]][0] : null)

  const menuWidth = 210

  return (
    <AnchoredOverlay
      x={screenX} y={screenY} margin={8}
      style={{
        width: menuWidth,
        maxHeight: 420,
        background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
        borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
        zIndex: 1000, fontFamily: 'var(--m-font)',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseDown={e => e.stopPropagation()}
      // Stop clicks inside the menu (search input, category headers) from
      // bubbling to the editor wrapper's onClick, which closes the menu. It
      // should only close on a node selection (onClose) or a click outside it.
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--m-line)', flexShrink: 0 }}>
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose()
            // The rendered order is `allItems` regrouped by category, so acting
            // on filtered[0] fired an entry other than the one shown first.
            if (e.key === 'Enter' && firstShown) {
              if (firstShown._match) onPickExistingNode?.(firstShown._match)
              else if (firstShown._exists) onFocusExisting?.(firstShown.type)
              else onAdd(firstShown.type, firstShown._functionId)
              onClose()
            }
          }}
          placeholder="Search nodes..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
            color: 'var(--m-fg-2)', padding: '4px 8px', borderRadius: 4,
            fontSize: 11, outline: 'none', fontFamily: 'var(--m-font)',
          }}
        />
      </div>

      <div style={{ overflowY: 'auto', padding: '4px 0' }}>
        {/* What is already HERE comes first: the report is that search only ever
            offered to add another one. Adding is still below, unchanged. */}
        <PaletteGraphMatches
          query={search}
          graphs={graphs}
          graph="event"
          onPick={(m) => { onPickExistingNode?.(m); onClose() }}
        />
        {categories.length === 0 && graphMatches.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 11, padding: '10px 12px' }}>No results</div>
        )}
        {categories.map(cat => (
          <div key={cat}>
            <div
              onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', cursor: 'pointer',
                color: 'var(--m-fg-3)', fontSize: 10, letterSpacing: 0.8,
                userSelect: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--m-fg-2)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--m-fg-3)'}
            >
              <span style={{ fontSize: 8 }}>{collapsed[cat] ? '▶' : '▼'}</span>
              {cat.toUpperCase()}
            </div>

            {!collapsed[cat] && byCategory[cat].map(item => (
              <div
                key={item._functionId ?? item.type}
                data-testid={`node-menu-item-${item.type}`}
                data-exists={item._exists ? 'true' : undefined}
                onClick={() => {
                  if (item._exists) onFocusExisting?.(item.type)
                  else onAdd(item.type, item._functionId)
                  onClose()
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--m-panel-hi)'
                  const r = e.currentTarget.getBoundingClientRect()
                  setTooltip({ item, x: r.right + 6, y: r.top })
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent'
                  setTooltip(null)
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  padding: '6px 10px 6px 22px', cursor: 'pointer',
                  borderLeft: `3px solid ${item.color}`,
                  marginLeft: 8, marginRight: 6,
                  borderRadius: '0 3px 3px 0',
                  background: 'transparent',
                  opacity: item._exists ? 0.5 : 1,
                }}
              >
                <span style={{ color: item.color, fontSize: 11 }}>{item.label}</span>
                {item._exists && (
                  <span style={{ color: 'var(--m-fg-3)', fontSize: 9, flexShrink: 0 }}>in graph ↗</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {tooltip && (
        <AnchoredOverlay portal x={tooltip.x} y={tooltip.y} style={{
          background: 'var(--m-panel)',
          border: `1px solid ${tooltip.item.color}44`,
          borderRadius: 4,
          padding: '6px 10px',
          zIndex: 1001,
          maxWidth: 190,
          pointerEvents: 'none',
        }}>
          <div style={{ color: tooltip.item.color, fontSize: 10, fontWeight: 'bold', marginBottom: 3, fontFamily: 'var(--m-font)' }}>
            {tooltip.item.label}
          </div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, fontFamily: 'var(--m-font)', lineHeight: 1.4 }}>
            {tooltip.item.desc}
          </div>
          {tooltip.item._exists && (
            <div style={{ color: 'var(--m-fg-2)', fontSize: 10, fontFamily: 'var(--m-font)', lineHeight: 1.4, marginTop: 4 }}>
              Already in graph (only one allowed) — click to jump to it.
            </div>
          )}
        </AnchoredOverlay>
      )}
    </AnchoredOverlay>
  )
}


// ─── EventCanvas — uses useReactFlow, must live inside ReactFlowProvider ──────
// ─── Event-graph clipboard host ─────────────────────────────────────────────
//
// The event graph has THREE canvases — the main graph, a graphGroup drill-in and
// a function body — and each owns its own nodes/edges state and its own undo
// history. This builds the clipboard host for one of them, so the three cannot
// disagree about what a paste does; only the state handles differ.
//
// Every paste is scoped to the canvas it happened on. A group's inner graph is
// its own namespace, and all three mint from the same ev_ counter
// (window.__evNodeId__), so a node pasted anywhere is unique everywhere.
function useEventClipboardHost({ setNodes, setEdges, history, nodesRef, edgesRef, variables = [], functions = {} }) {
  const applyPaste = useCallback((pastedNodes, pastedEdges) => {
    history.push(nodesRef.current, edgesRef.current)
    // Paste selects what it pasted, so an immediate drag or second paste acts
    // on the new copy rather than the original (Unreal's behaviour).
    setNodes(ns => [
      ...(ns ?? []).map(n => (n.selected ? { ...n, selected: false } : n)),
      ...pastedNodes.map(n => ({ ...n, selected: true })),
    ])
    if (pastedEdges?.length) setEdges(es => [...(es ?? []), ...pastedEdges])
  }, [history, nodesRef, edgesRef, setNodes, setEdges])

  const removeNodes = useCallback((ids) => {
    const kill = new Set(ids)
    history.push(nodesRef.current, edgesRef.current)
    setNodes(ns => (ns ?? []).filter(n => !kill.has(n.id)))
    setEdges(es => (es ?? []).filter(e => !kill.has(e.source) && !kill.has(e.target)))
  }, [history, nodesRef, edgesRef, setNodes, setEdges])

  return useMemo(() => ({
    kind: 'events',
    source: { character: resolveCharacterId?.() ?? null, graph: 'events' },
    getDeclarations: () => ({ variables, functions }),
    getAvailable: () => availableRefsFor('events', { variables, functions }),
    // Seeded from the LIVE graph per paste, with the shared window counter as a
    // FLOOR so ids stay unique against both mechanisms at once.
    // Seeded from the LIVE graph per paste, with the shared window counter as a
    // FLOOR so ids stay unique against both mechanisms — and the counter is
    // pushed PAST what we mint, or the next nextEvId() from the palette would
    // hand back an id a paste just used.
    makeMinter: () => {
      const mint = createMinter(nodesRef.current, 'ev', { floor: window.__evNodeId__ ?? 1 })
      return () => {
        const id = mint()
        const n = Number.parseInt(id.slice(3), 10)
        if (Number.isFinite(n)) window.__evNodeId__ = Math.max(window.__evNodeId__ ?? 1, n + 1)
        return id
      }
    },
    applyPaste,
    removeNodes,
  }), [variables, functions, applyPaste, removeNodes])
}

function EventCanvas({ nodesWithHandlers, edges, onNodesChange, onEdgesChange, onConnect, onBeforeDelete, addNodeAt, focusRef, onDeleteNode, onDuplicateNode, onArrange, clipboardHost = null, isFunctionEditor = false, isGroupEditor = false, functionDefs = [], extraExistingTypes, focusContainerId = null }) {
  const { screenToFlowPosition, setCenter, getNode, getNodes, getEdges, fitView } = useReactFlow()
  // Serves a Ctrl+F / agent focus request scoped to exactly this canvas — the
  // top-level graph (null) or the inside of one group / function.
  // Keep this canvas's framing across a close (see useGraphViewport). Scoped to
  // the container, because the function and group drill-ins are their own
  // ReactFlow instances with their own coordinate space.
  const viewportProps = useRememberedViewport({ surface: 'events', containerId: focusContainerId })

  useCanvasFocus({
    graph: 'event',
    containerId: focusContainerId,
    onSelectNode: useCallback(
      (id) => onNodesChange(selectOnlyChanges(nodesWithHandlers, id)),
      [onNodesChange, nodesWithHandlers],
    ),
  })
  const [contextMenu, setContextMenu] = useState(null)
  const [nodeMenu,    setNodeMenu]    = useState(null)

  // ── Clipboard (copy / cut / paste) ────────────────────────────────────────
  // Assembled here because only this component sees the live selection (the
  // parent's controlled `nodes` carry no `selected` flag). Mutations land in
  // the parent's applyPaste / removeNodes so they ride its setNodes → module
  // graph-ref sync, autosave and undo history.
  //
  // ONE hook serves all three event canvases — the main graph, a graphGroup
  // drill-in and a function body — because each mounts its own EventCanvas with
  // its own host. That is also why paste must be scoped: a group's inner graph
  // is its own namespace, and its host mints ids from the same ev_ counter.
  const flowApiRef = useRef(null)
  flowApiRef.current = { screenToFlowPosition }
  const clipboard = useGraphClipboard(
    useMemo(() => ({
      ...(clipboardHost ?? {}),
      getGraph: () => ({ nodes: getNodes(), edges: getEdges() }),
    }), [clipboardHost, getNodes, getEdges]),
    // Each event canvas is its own surface: the main graph, a group drill-in and
    // a function body are all mounted at once, so an unscoped message would
    // caption every one of them.
    { surface: focusContainerId ? `events:${focusContainerId}` : 'events', enabled: !!clipboardHost, flowRef: flowApiRef },
  )

  // Expose focus function to the left panel
  focusRef.current = useCallback((nodeId) => {
    const node = getNode(nodeId)
    if (!node) return
    setCenter(
      node.position.x + (node.measured?.width  ?? 200) / 2,
      node.position.y + (node.measured?.height ?? 80)  / 2,
      { zoom: 1.1, duration: 350 },
    )
  }, [getNode, setCenter])

  // ── Arrange (ENG-1069) ────────────────────────────────────────────────────
  // Same split as the anim editor: compute from the measured/selected nodes
  // here, apply through the parent's setNodes (auto-save + undo). Shared by the
  // top-level / function / group canvases via their own onArrange handlers.
  const runArrange = useCallback((op) => {
    if (!onArrange) return
    const geo = getNodes().map(n => ({
      id: n.id,
      position: n.position,
      width: n.measured?.width ?? n.width,
      height: n.measured?.height ?? n.height,
      selected: n.selected,
    }))
    const selIds = geo.filter(n => n.selected).map(n => n.id)
    let patches = []
    if (op === 'tidy') patches = tidyGraph(geo, edges, {})
    else if (op === 'straighten') patches = straightenConnections(geo, edges, selIds)
    else if (op.startsWith('align:')) patches = alignNodes(geo, selIds, op.slice(6))
    else if (op.startsWith('distribute:')) patches = distributeNodes(geo, selIds, op.slice(11))
    if (patches.length === 0) return
    onArrange(patches)
    if (op === 'tidy') setTimeout(() => fitView({ padding: 0.4, duration: 300 }), 40)
  }, [onArrange, getNodes, edges, fitView])

  const hoveredRef = useRef(false)
  useEffect(() => {
    const onKey = (e) => {
      if (!hoveredRef.current) return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const op = arrangeShortcutFor(e)
      if (!op) return
      e.preventDefault()
      runArrange(op)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [runArrange])

  // extraExistingTypes: GLOBAL_UNIQUE_TYPES (voxyConfig/convorcherSession) already present in a
  // DIFFERENT pool (top-level vs. a group) — greyed here too so the palette doesn't offer a
  // duplicate that addNodeAt would silently refuse anyway. See globalUniqueTypesInUse.
  const existingTypes = new Set([...nodesWithHandlers.map(n => n.type), ...(extraExistingTypes ?? [])])

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    setNodeMenu(null)
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  // Toolbar "Search" opens the same add-node palette as a right-click.
  const openPalette = useCallback((e) => {
    e.stopPropagation() // else the wrapper's onClick closes the menu we just opened
    setNodeMenu(null)
    const x = e.clientX, y = e.clientY
    setContextMenu({ screenX: x, screenY: y, flowPos: screenToFlowPosition({ x, y }) })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])
  const closeNodeMenu = useCallback(() => setNodeMenu(null), [])

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu(null)
    setNodeMenu({ screenX: e.clientX, screenY: e.clientY, nodeId: node.id, deletable: node.deletable !== false })
  }, [])

  const handleAdd = useCallback((type, functionId) => {
    if (contextMenu) addNodeAt(type, contextMenu.flowPos, functionId)
  }, [contextMenu, addNodeAt])

  // Jump to the existing instance of a unique node type (menu shows those
  // greyed instead of hiding them — see ContextMenu). Through requestFocus, not
  // focusRef: a unique node is allowed to live one level inside a graphGroup
  // (globalUniqueTypesInUse scans for exactly that), and only requestFocus
  // drills in. focusRef could only ever serve a node on THIS canvas.
  const handleFocusExisting = useCallback((type) => {
    const hit = findGraphNodes(readGraphs(), type, { preferGraph: 'event' })
      .find(m => m.graph === 'event' && m.type === type)
    if (hit) requestFocus({ graph: 'event', nodeId: hit.nodeId, container: hit.container, label: hit.label }).catch(() => {})
  }, [])

  // Go to a node the user picked out of the "IN THIS GRAPH" section.
  const handlePickExistingNode = useCallback((m) => {
    requestFocus({ graph: 'event', nodeId: m.nodeId, container: m.container, label: m.label }).catch(() => {})
  }, [])

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onClick={() => { closeMenu(); closeNodeMenu() }}
      onMouseEnter={() => { hoveredRef.current = true }}
      onMouseLeave={() => { hoveredRef.current = false }}
      onPointerMove={clipboard.onPointerMove}
      onPaste={clipboard.onPaste}
    >
      <ReactFlow
        nodes={nodesWithHandlers}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onBeforeDelete={onBeforeDelete}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        nodeTypes={EVENT_NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        {...viewportProps}
        fitViewOptions={{ padding: 0.4 }}
        deleteKeyCode="Delete"
        style={{ background: 'var(--m-bg-graph)' }}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color="var(--m-line-hi)" gap={24} size={1} />
        <Controls style={{ background: 'var(--m-panel)', border: '1px solid var(--m-line)' }} />
        <MiniMap
          // Display-only — same rule as BlueprintEditor's minimap: without
          // pointerEvents none, nodes under the bottom-right corner are
          // silently unclickable (the panel intercepts the pointer).
          style={{ background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)', pointerEvents: 'none' }}
          nodeColor={n => {
            if (n.type === 'beginPlay' || n.type === 'onConstruct')       return '#8b1a1a'
            if (n.type === 'printString')                                 return '#1a3060'
            if (n.type === 'playFirstSentence' || n.type === 'playNextSentence' || n.type === 'sendTextInput' || n.type === 'sendVoiceTextMessage' || n.type === 'delay' || n.type === 'parlaySTT' || n.type === 'voxyConfig' || n.type === 'convorcherSession') return '#0a4028'
            if (n.type === 'inputKey') return '#7a3010'
            if (n.type === 'graphGroup')                                  return '#123840'
            if (EVENT_TYPES.has(n.type))                                  return '#7a3010'
            return '#334'
          }}
        />
      </ReactFlow>

      {contextMenu && (
        <ContextMenu
          screenX={contextMenu.screenX}
          screenY={contextMenu.screenY}
          existingTypes={existingTypes}
          onAdd={handleAdd}
          onFocusExisting={handleFocusExisting}
          onPickExistingNode={handlePickExistingNode}
          onClose={closeMenu}
          isFunctionEditor={isFunctionEditor}
          isGroupEditor={isGroupEditor}
          functionDefs={functionDefs}
        />
      )}
      {nodeMenu && (
        <NodeContextMenu
          screenX={nodeMenu.screenX}
          screenY={nodeMenu.screenY}
          nodeId={nodeMenu.nodeId}
          deletable={nodeMenu.deletable}
          onDelete={onDeleteNode}
          onDuplicate={onDuplicateNode}
          onArrange={runArrange}
          onCopy={clipboardHost ? clipboard.doCopy : undefined}
          onCut={clipboardHost ? clipboard.doCut : undefined}
          onClose={closeNodeMenu}
        />
      )}

      <GraphClipboardStatus status={clipboard.status} />

      {/* Hint */}
      <GraphToolbar
        hint="right-click to add · Del to remove · Ctrl+C/X/V to copy nodes"
        onSearch={openPalette}
        onTidy={() => runArrange('tidy')}
      />
    </div>
  )
}

const nextFnId = () => `fn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`

// ─── Default node data ─────────────────────────────────────────────────────────
// Shared by the main graph's addNodeAt AND a graph group's inner addNodeAt (camera-in-blueprint
// Phase 3) — a group's inner graph accepts the same node catalog as the main graph (full
// palette minus 'graphGroup'/rig templates, see ContextMenu's isGroupEditor filter), so both
// call sites need the identical default-data table. Rig-template palette entries and
// 'graphGroup' itself produce a whole node (or cluster), not a single node's data, so their
// callers special-case them BEFORE reaching this function.
function defaultNodeData(type, functionId) {
  let data = {}
  if (type === 'print' || type === 'printString') data = { duration: 5, color: '#ffff00' }
  if (type === 'playFaceClip')  data = { clipName: 'listening_neutral_face' }
  if (type === 'playBodyClip')  data = { clipName: '', playSpeed: 1.0 }
  if (type === 'loadEnvironment') data = { environmentId: '', environmentName: '', format: '' }
  // Defaults mirror liveTuneStore.DEFAULTS.backdrop, so a hand-added node that
  // nobody edits applies the same backdrop the app already had — an inert node,
  // not a surprise repaint.
  if (type === 'setBackdrop')     data = { skyEnabled: false, skyColor: BACKDROP_SKY, groundEnabled: false, groundColor: BACKDROP_GROUND, groundSize: 20, hideEnvironment: false }
  if (type === 'setVariable') data = { varName: '', varValue: '' }
  if (type === 'random') data = { randomType: 'float', min: 0, max: 1 }
  if (type === 'select') data = { selectType: 'string', optionCount: 2, index: 0 }
  if (type === 'concatString') data = { count: 2 }
  if (type === 'makeLiteralString') data = { value: '' }
  if (type === 'makeLiteralFloat')  data = { value: 0.0 }
  if (type === 'makeLiteralInt')    data = { value: 0 }
  if (type === 'getVariable')       data = { varName: '' }
  if (type === 'customEvent')       data = { name: '' }
  // TArray
  if (type === 'arrayGet')    data = { varName: '', index: 0 }
  if (type === 'arraySet')    data = { varName: '', index: 0, value: '' }
  if (type === 'arrayAdd')    data = { varName: '', value: '' }
  if (type === 'arrayRemove') data = { varName: '', index: 0 }
  if (type === 'arrayLength') data = { varName: '' }
  if (type === 'arrayClear')  data = { varName: '' }
  // TMap
  if (type === 'mapGet')      data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapSet')      data = { varName: '', key: '', keyType: 'string', value: '' }
  if (type === 'mapRemove')   data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapHasKey')   data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapLength')   data = { varName: '' }
  if (type === 'functionCall') data = { functionId: functionId ?? '' }
  // Wayfinding / movement (ENG-592)
  if (type === 'moveToLocation')    data = { x: 0, y: 0, z: 0, acceptanceRadius: 0.2 }
  if (type === 'moveToActor')       data = { target: '', acceptanceRadius: 0.2 }
  if (type === 'moveToRandomPoint') data = { centerX: 0, centerY: 0, centerZ: 0, radius: 3 }
  // Camera (camera-in-blueprint)
  if (type === 'setCameraFov')        data = { fov: 45, blendMs: 0 }
  if (type === 'setCameraTarget')     data = { mode: 'vector', x: 0, y: 1.5, z: 0, blendMs: 0 }
  if (type === 'setCameraCage')       data = { enabled: true, hx: 2.5, hy: 1.25, hz: 2.5, margin: 0.5 }
  if (type === 'setCameraSeeThrough') data = { enabled: true, radius: 0.6, fade: 0.3, auto: false, autoNear: 1.2, autoBelow: true }
  if (type === 'setCameraFollow')     data = { enabled: true, x: 0, y: 1.6, z: 3, space: 'local', damping: 0.12, targetOffsetY: 0 }
  // Camera rig motion. Sensitivity defaults are tuned for raw canvas pixels in:
  // 0.005 rad/px ≈ a 180° orbit across ~630px of drag; dolly 0.001 ≈ e^0.1 per wheel notch.
  if (type === 'cameraAddYaw')        data = { delta: 0, sensitivity: 0.005, invert: false }
  if (type === 'cameraAddPitch')      data = { delta: 0, sensitivity: 0.005, invert: false }
  if (type === 'cameraAddDolly')      data = { delta: 0, sensitivity: 0.001, invert: false }
  if (type === 'cameraPanTarget')     data = { dx: 0, dy: 0, sensitivity: 0.0015 }
  if (type === 'cameraSetRigLimits')  data = { minDist: 0.6, maxDist: 0, minPitch: -89, maxPitch: 89 }
  if (type === 'cameraSetRigPose')    data = { yaw: 0, pitch: 10, dist: 3, x: 0, y: 1.35, z: 0, blendMs: 0 }
  if (type === 'headTrackedCamera')   data = { ...HEAD_TRACK_DEFAULTS }
  // User input
  if (type === 'onPointerDown')       data = { button: 'left' }
  if (type === 'onPointerUp')         data = { button: 'left' }
  if (type === 'onPointerDrag')       data = { button: 'left' }
  if (type === 'onWheel')             data = {}
  if (type === 'onKeyDown')           data = { key: '' }
  if (type === 'onKeyUp')             data = { key: '' }
  if (type === 'isButtonHeld')        data = { button: 'left' }
  if (type === 'isKeyHeld')           data = { key: '' }
  if (type === 'getInputAxis')        data = { axis: 'mouseX' }
  // Groups (camera-in-blueprint Phase 3) — empty container, no exec/data pins in v1.
  if (type === 'graphGroup')          data = { name: 'Graph Group', innerNodes: [], innerEdges: [] }
  return data
}

// ─── Main EventGraph component ────────────────────────────────────────────────
export function EventGraph({
  variables, onVariablesChange,
  eventNodes:      initialEventNodes,
  eventEdges:      initialEventEdges,
  onEventNodesChange,
  onEventEdgesChange,
  functions:        initialFunctions = {},
  onFunctionsChange,
  nodeLastFired = {},
}) {
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState('bool')
  // ── Function view / Group view ── mutually exclusive drill-in states (camera-in-blueprint
  // Phase 3 generalizes the function drill-in to graph groups — see openFunction/openGroup).
  const [funcView, setFuncView] = useState(null)   // null = main graph, functionId string = function editor
  const [groupView, setGroupView] = useState(null) // null = main graph, top-level node id = group editor
  const openFunction = useCallback((id) => { setGroupView(null); setFuncView(id) }, [])
  const openGroup    = useCallback((id) => { setFuncView(null); setGroupView(id) }, [])
  const [newItemType,  setNewItemType]  = useState('string')
  const [newKeyType,   setNewKeyType]   = useState('string')
  const [newValueType, setNewValueType] = useState('string')
  const [editingVarId, setEditingVarId] = useState(null)

  // ── Canvas state ──
  const [nodes, setNodes, onNodesChange] = useNodesState(initialEventNodes ?? INITIAL_EVENT_NODES)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEventEdges ?? INITIAL_EVENT_EDGES)
  const focusRef = useRef(() => {})

  // Ctrl+F / agent focus: drill into the container holding the target (a graph
  // group or a function), or pop back out to the main graph for a top-level
  // node — otherwise a request can never be served while the user is sitting
  // inside a sub-graph, and it would time out looking like a broken search.
  const currentContainerId = useCallback(() => funcView ?? groupView ?? null, [funcView, groupView])
  const nodesRef = useRef(nodes); nodesRef.current = nodes
  const openFocusContainer = useCallback((containerId) => {
    if (!containerId) { setFuncView(null); setGroupView(null); return }
    // A function id and a group's node id come from different pools; ask the
    // live state which this is rather than guessing, and do nothing for an id
    // that is neither (the request then times out honestly).
    if (initialFunctions?.[containerId]) openFunction(containerId)
    else if (nodesRef.current.some(n => n.id === containerId && n.type === 'graphGroup')) openGroup(containerId)
  }, [initialFunctions, openFunction, openGroup])
  useFocusDrill({ graph: 'event', onOpenContainer: openFocusContainer, currentContainerId })

  // Advance ID counter past all saved ev_* IDs (main graph + function bodies)
  useEffect(() => {
    const advance = (id) => {
      const m = id?.match(/^ev_(\d+)$/)
      if (m) window.__evNodeId__ = Math.max(window.__evNodeId__, parseInt(m[1]) + 1)
    }
    for (const n of nodes) {
      advance(n.id)
      // One level into graphGroup containers (camera-in-blueprint Phase 3) — inner nodes are
      // freshly minted via the same nextEvId() counter (GroupEditor's addNodeAt), so a saved
      // graph whose group holds the highest ev_ id must still advance the counter past it.
      if (n.type === 'graphGroup') for (const inner of n.data?.innerNodes ?? []) advance(inner.id)
    }
    for (const fn of Object.values(initialFunctions)) {
      for (const n of fn.nodes ?? []) advance(n.id)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync upward + to eventGraphState
  useEffect(() => {
    onEventNodesChange?.(nodes)
    eventGraphState.nodes = nodes
  }, [nodes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Merge a node inserted from outside the editor (CameraRigNotice's live "Add Orbit Rig")
  // into the canvas so it shows immediately; the [nodes] effect above then flows it upward and
  // back into eventGraphState. Append-and-dedup — never touches existing nodes.
  useEffect(() => subscribeEventGraphInsert(node => {
    setNodes(ns => ns.some(n => n.id === node.id) ? ns : [...ns, node])
  }), [setNodes])

  useEffect(() => {
    onEventEdgesChange?.(edges)
    eventGraphState.edges = edges
  }, [edges]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    eventGraphState.functions = initialFunctions
  }, [initialFunctions])

  // Inject onChange into node data
  const updateNodeData = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setNodes])

  // Patch a top-level graphGroup node's inline data (innerNodes/innerEdges/name) — the sync
  // target GroupEditor writes through, mirroring updateFunctionDef for functions.
  const updateGroupNode = useCallback((nodeId, patch) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n
    ))
  }, [setNodes])

  const functionDefs = Object.values(initialFunctions)

  const nodesWithHandlers = useStableNodeData(nodes, n => ({
    onChange:          (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired:   nodeLastFired[n.id] ?? 0,
    variables,
    functions:         initialFunctions,
    onOpenFunction:    n.type === 'functionCall' ? () => openFunction(n.data.functionId) : undefined,
    onOpenGroup:       n.type === 'graphGroup'   ? () => openGroup(n.id)                 : undefined,
  }), [nodeLastFired, variables, initialFunctions, updateNodeData, openFunction, openGroup])

  const onConnect = useCallback((params) => {
    // Unreal capacity rules (ENG-877): displace the old wire instead of
    // keeping both — see graphConnectPolicy.
    setEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setEdges])

  // ── Undo history (ENG-878) ──
  const history = useRef(createGraphHistory()).current
  const nodesRefU = useRef(nodes); nodesRefU.current = nodes
  const edgesRefU = useRef(edges); edgesRefU.current = edges

  // One snapshot per delete GESTURE (Delete key / edge ×): onBeforeDelete
  // fires once with the full pre-delete state.
  const onBeforeDelete = useCallback(async () => {
    history.push(nodesRefU.current, edgesRefU.current)
    return true
  }, [history])

  // Arrange (ENG-1069): apply the child-computed position patch through
  // setNodes so it rides the same auto-save + undo path as any edit.
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(nodesRefU.current, edgesRefU.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setNodes])

  // Ctrl/Cmd+Z restores the last deletion. Guarded to the MAIN canvas —
  // the function/group drill-ins own their state and undo — and to
  // non-input targets so text fields keep native undo.
  const drillRef = useRef(null); drillRef.current = funcView || groupView
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      if (drillRef.current) return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setNodes(snap.nodes)
      setEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setNodes, setEdges])

  // ── Add node at flow position (from right-click) ──
  const addNodeAt = useCallback((type, position, functionId) => {
    // Enforce uniqueness for nodes marked unique in palette. Event-category unique types are
    // scoped to nodes visible on THIS canvas (top-level here); GLOBAL_UNIQUE_TYPES
    // (voxyConfig/convorcherSession) must ALSO not already exist inside any graphGroup.
    const palEntry = EVENT_PALETTE.find(p => p.type === type)
    if (palEntry?.unique &&
        (nodes.some(n => n.type === type) || globalUniqueTypesInUse(nodes).has(type))) return

    // Camera rig templates ("presets, as graphs") now insert ONE self-contained graphGroup node
    // (camera-in-blueprint Phase 3) rather than a spliced cluster — after which it's just a
    // group you can open and edit. This is the whole replacement for the deleted
    // setCameraPreset enum. instantiateRigAsGroup mints the group its own inner beginPlay and
    // chains the setup nodes (limits/pose) through it, so they still fire on load — see that
    // function's doc comment in cameraRigTemplates.js.
    if (palEntry?.rig) {
      const tpl = CAMERA_RIG_TEMPLATES[palEntry.rig]
      if (!tpl) { console.warn(`[EventGraph] unknown camera rig template "${palEntry.rig}"`); return }
      const groupNode = instantiateRigAsGroup(tpl, position, nextEvId)
      setNodes(ns => [...ns, groupNode])
      devLog(`[EventGraph] inserted "${tpl.label}" as a graph group — ${groupNode.data.innerNodes.length} nodes, ${groupNode.data.innerEdges.length} edges`)
      return
    }

    const data = defaultNodeData(type, functionId)
    setNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [nodes, setNodes])

  const clipboardHost = useEventClipboardHost({
    setNodes, setEdges, history, nodesRef: nodesRefU, edgesRef: edgesRefU,
    variables, functions: initialFunctions,
  })

  const deleteNode = useCallback((id) => {
    // Context-menu delete bypasses ReactFlow's delete pipeline — snapshot
    // here so Ctrl+Z restores it too (ENG-878).
    history.push(nodesRefU.current, edgesRefU.current)
    setNodes(ns => ns.filter(n => n.id !== id))
    setEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setNodes, setEdges, history])

  const duplicateNode = useCallback((id) => {
    setNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, variables: _v, ...cleanData } = node.data
      return [...ns, {
        ...node,
        id: nextEvId(),
        position: { x: node.position.x + 40, y: node.position.y + 40 },
        data: cleanData,
        selected: false,
      }]
    })
  }, [setNodes])

  // ── Functions ──
  const createFunction = () => {
    const id   = nextFnId()
    const name = `NewFunction`
    const entryNode = { id: 'entry', type: 'functionEntry', position: { x: 80, y: 120 }, data: {}, deletable: false }
    const returnNode = { id: `${id}_ret`, type: 'functionReturn', position: { x: 400, y: 120 }, data: {} }
    const fn = { id, name, inputs: [], outputs: [], nodes: [entryNode, returnNode], edges: [] }
    onFunctionsChange?.({ ...initialFunctions, [id]: fn })
    openFunction(id)
  }

  const deleteFunction = (id) => {
    const updated = { ...initialFunctions }
    delete updated[id]
    onFunctionsChange?.(updated)
    if (funcView === id) setFuncView(null)
  }

  const renameFunction = (id, name) => {
    if (!name.trim()) return
    onFunctionsChange?.({ ...initialFunctions, [id]: { ...initialFunctions[id], name: name.trim() } })
  }

  const updateFunctionDef = (id, patch) => {
    onFunctionsChange?.({ ...initialFunctions, [id]: { ...initialFunctions[id], ...patch } })
  }

  // ── Variables ──
  const addVariable = () => {
    const name = newName.trim()
    if (!name || variables.find(v => v.name === name)) return
    const base = { id: nextVarId(), name, type: newType, defaultValue: TYPE_DEFAULTS[newType], value: TYPE_DEFAULTS[newType] }
    if (newType === 'array') base.itemType = newItemType
    if (newType === 'map')   { base.keyType = newKeyType; base.valueType = newValueType }
    onVariablesChange([...variables, base])
    setNewName('')
  }

  const removeVariable = (id) => onVariablesChange(variables.filter(v => v.id !== id))

  const updateValue = (id, raw) => {
    onVariablesChange(variables.map(v => {
      if (v.id !== id) return v
      let value = raw
      if (v.type === 'bool')   value = raw === 'true' || raw === true
      if (v.type === 'float')  value = parseFloat(raw) || 0
      if (v.type === 'int')    value = parseInt(raw)   || 0
      if (v.type === 'string') value = String(raw)
      // array / map: mutated at runtime by nodes — never coerce from the panel
      return { ...v, value }
    }))
  }

  const updateVarName = (id, name) => {
    if (!name.trim()) return
    if (variables.find(v => v.name === name && v.id !== id)) return
    onVariablesChange(variables.map(v => v.id === id ? { ...v, name: name.trim() } : v))
  }

  // ── Derived: MY BLUEPRINT rail contents ──
  // One walk (graphNodeSearch.collectGraphNodes) so the rail, Ctrl+F and the
  // palette can never disagree about what is in the graph. It reaches one level
  // into graphGroups and into function bodies, which the old `nodes.filter` did
  // not -- a node in the Orbit Camera Rig group was simply absent from the rail.
  const outlineGraphs = { event: { nodes, functions: initialFunctions } }
  const eventGroups  = listNodesByType(outlineGraphs, { graph: 'event' })
    .filter(g => EVENT_TYPES.has(g.type))
  // Everything that is not an event starter. Actions were never listed at all,
  // so half a blueprint had no entry in its own outline.
  const actionGroups = listNodesByType(outlineGraphs, { graph: 'event' })
    .filter(g => !EVENT_TYPES.has(g.type))

  const labelForType = useCallback(
    (type) => EVENT_PALETTE.find(p => p.type === type)?.label ?? type,
    [],
  )

  // Navigate via the focus store, NOT the local focusRef: requestFocus ->
  // useFocusDrill opens the containing group or function first, which is what
  // makes a nested node reachable at all. It also fixes a latent dead click --
  // while a group/function view is open the top-level focusRef still holds a
  // closure over an unmounted ReactFlow instance, so clicking a rail row did
  // nothing and said nothing.
  const focusOutlineNode = useCallback((nodeId) => {
    const hit = [...eventGroups, ...actionGroups].flatMap(g => g.nodes).find(n => n.nodeId === nodeId)
    requestFocus({ graph: 'event', nodeId, container: hit?.container ?? null, label: hit?.label })
      .catch(() => {})
  }, [eventGroups, actionGroups])

  // Currently-open group's top-level node, if any (groupView holds its id — see openGroup).
  // Looked up from live `nodes` every render, same pattern FunctionEditor uses via
  // initialFunctions[funcView], so GroupEditor always sees the latest innerNodes/innerEdges.
  const groupNode = groupView ? nodes.find(n => n.id === groupView && n.type === 'graphGroup') : null

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--m-bg)', fontFamily: 'var(--m-font)' }}>

      {/* ─── MY BLUEPRINT left panel ──────────────────────────────── */}
      <div style={{
        width: 230,
        background: 'var(--m-panel)',
        borderRight: '1px solid var(--m-line)',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 11,
        flexShrink: 0,
      }}>
        {/* Panel title */}
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--m-line)',
          color: 'var(--m-fg-3)',
          fontSize: 10,
          letterSpacing: 1.5,
          fontWeight: 'bold',
        }}>
          MY BLUEPRINT
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* ── EVENTS section ── */}
          <Section label="EVENTS">
            <BlueprintOutlineSection
              label="EVENTS"
              groups={eventGroups}
              labelForType={labelForType}
              onFocus={focusOutlineNode}
              emptyHint="Right-click canvas to add events"
            />
          </Section>

          {/* ── ACTIONS section ── every non-event node in the graph, including
              the ones inside groups and functions. Without this the outline
              described only the starters and nothing they drove. */}
          <Section label="ACTIONS">
            <BlueprintOutlineSection
              label="ACTIONS"
              groups={actionGroups}
              labelForType={labelForType}
              onFocus={focusOutlineNode}
              emptyHint="Right-click canvas to add nodes"
              glyph="◆"
              accent={ACTIONS_ACCENT}
            />
          </Section>

          {/* ── FUNCTIONS section ── */}
          <Section label="FUNCTIONS" action={{ label: '＋', onClick: createFunction, title: 'New function' }}>
            {functionDefs.length === 0 ? (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No functions defined.
              </div>
            ) : functionDefs.sort((a, b) => a.name.localeCompare(b.name)).map(fn => (
              <FunctionSidebarRow
                key={fn.id}
                fn={fn}
                active={funcView === fn.id}
                onOpen={() => openFunction(fn.id)}
                onDelete={() => deleteFunction(fn.id)}
              />
            ))}
          </Section>

          {/* ── VARIABLES section ── */}
          <Section label="VARIABLES">
            {variables.length === 0 && (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No variables defined.
              </div>
            )}
            {variables.map(v => (
              <VariableRow
                key={v.id}
                variable={v}
                onNameChange={name => updateVarName(v.id, name)}
                onValueChange={val  => updateValue(v.id, val)}
                onRemove={() => removeVariable(v.id)}
                onEdit={(v.type === 'array' || v.type === 'map') ? () => setEditingVarId(v.id) : undefined}
              />
            ))}
          </Section>

          {/* ── CONSTANTS section (read-only) ── */}
          <Section label="CONSTANTS">
            {Object.keys(constants).length === 0 ? (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No constants set.
              </div>
            ) : Object.entries(constants).map(([name, value]) => (
              <div key={name} style={{
                display: 'flex', alignItems: 'center',
                padding: '4px 10px', gap: 5,
                borderBottom: '1px solid var(--m-line)',
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#c060e8', flexShrink: 0 }} />
                <span style={{ flex: 1, color: 'var(--m-fg-2)', fontSize: 10, fontFamily: 'var(--m-mono)' }}>{name}</span>
                <span style={{ color: 'var(--m-fg-3)', fontSize: 9, fontFamily: 'var(--m-mono)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {String(value)}
                </span>
              </div>
            ))}
          </Section>
        </div>

        {/* Add variable footer */}
        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--m-line)', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1 }}>ADD VARIABLE</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addVariable()}
              placeholder="name"
              style={{
                flex: 1, background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                color: 'var(--m-fg-2)', borderRadius: 3, padding: '3px 5px',
                fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
              }}
            />
            <select
              value={newType}
              onChange={e => setNewType(e.target.value)}
              style={{
                background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                color: TYPE_COLOR[newType], borderRadius: 3,
                padding: '3px 5px', fontSize: 10, cursor: 'pointer',
              }}
            >
              <option value="bool">bool</option>
              <option value="float">float</option>
              <option value="int">int</option>
              <option value="string">string</option>
              <option value="array">array</option>
              <option value="map">map</option>
            </select>
          </div>
          {newType === 'array' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>item type</span>
              <select
                value={newItemType}
                onChange={e => setNewItemType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newItemType], borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="bool">bool</option>
                <option value="float">float</option>
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
            </div>
          )}
          {newType === 'map' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>key</span>
              <select
                value={newKeyType}
                onChange={e => setNewKeyType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newKeyType] ?? '#d0a060', borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>value</span>
              <select
                value={newValueType}
                onChange={e => setNewValueType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newValueType], borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="bool">bool</option>
                <option value="float">float</option>
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
            </div>
          )}
          <button
            onClick={addVariable}
            style={{
              background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
              color: '#3060a0', borderRadius: 3, padding: '4px',
              fontSize: 10, fontFamily: 'var(--m-font)', cursor: 'pointer',
            }}
          >
            + Add Variable
          </button>
        </div>
      </div>

      {/* ─── Event canvas / Function editor ──────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top bar: simulate button or function breadcrumb */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px',
          background: 'var(--m-panel)',
          borderBottom: '1px solid var(--m-line)',
          flexShrink: 0,
        }}>
          {funcView ? (
            <>
              <button
                onClick={() => setFuncView(null)}
                style={{
                  background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                  color: '#5080a0', borderRadius: 4,
                  padding: '4px 10px', fontSize: 10,
                  fontFamily: 'var(--m-font)', cursor: 'pointer',
                }}
              >← Main Graph</button>
              <span style={{ color: '#3a2a5a', fontSize: 9 }}>›</span>
              <span style={{ color: '#9060d0', fontSize: 10, fontFamily: 'var(--m-font)' }}>
                {initialFunctions[funcView]?.name ?? funcView}
              </span>
            </>
          ) : groupNode ? (
            <>
              <button
                onClick={() => setGroupView(null)}
                style={{
                  background: '#0a0e14', border: '1px solid #123840',
                  color: '#4fa0a8', borderRadius: 4,
                  padding: '4px 10px', fontSize: 10,
                  fontFamily: 'var(--m-font)', cursor: 'pointer',
                }}
              >← Main Graph</button>
              <span style={{ color: '#123840', fontSize: 9 }}>›</span>
              {/* Named so a test can assert "the breadcrumb says which group
                  we drilled into" without a bare text match: since 5fcd0978
                  every MY BLUEPRINT row also prints its container's name, so
                  the group name appears many times on screen. */}
              <span data-testid="group-breadcrumb" style={{ color: '#4fc0c8', fontSize: 10, fontFamily: 'var(--m-font)' }}>
                ▣ {groupNode.data?.name ?? 'Graph Group'}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--m-fg-3)', fontSize: 9, fontFamily: 'var(--m-font)' }}>
              right-click canvas to add nodes
            </span>
          )}
        </div>

        {funcView && initialFunctions[funcView] ? (
          <FunctionEditor
            key={funcView}
            fnDef={initialFunctions[funcView]}
            functions={initialFunctions}
            functionDefs={functionDefs}
            nodeLastFired={nodeLastFired}
            variables={variables}
            onFnDefChange={patch => updateFunctionDef(funcView, patch)}
          />
        ) : groupNode ? (
          <GroupEditor
            key={groupNode.id}
            groupNode={groupNode}
            functions={initialFunctions}
            functionDefs={functionDefs}
            nodeLastFired={nodeLastFired}
            onGroupNodeChange={patch => updateGroupNode(groupNode.id, patch)}
            topNodes={nodes}
            variables={variables}
            openFunction={openFunction}
          />
        ) : (
          <div style={{ flex: 1, position: 'relative' }}>
            <ReactFlowProvider>
              <EventCanvas
                nodesWithHandlers={nodesWithHandlers}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onBeforeDelete={onBeforeDelete}
                onArrange={onArrange}
                addNodeAt={addNodeAt}
                focusRef={focusRef}
                onDeleteNode={deleteNode}
                onDuplicateNode={duplicateNode}
                clipboardHost={clipboardHost}
                functionDefs={functionDefs}
                extraExistingTypes={globalUniqueTypesInUse(nodes)}
              />
            </ReactFlowProvider>
            {/* Shown only while this blueprint drives no camera (mirrors the live-mode
                CameraRigNotice). One click inserts the Orbit rig as a graph group via the same
                addNodeAt('rigOrbit') path as the palette, so it's saved with the blueprint. */}
            {cameraRigState(nodes) !== 'ok' && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                zIndex: 6, display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(20,26,34,0.94)', border: `1px solid ${CAMERA_ACCENT}`, borderRadius: 6,
                padding: '6px 10px 6px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
              }}>
                <span style={{ color: '#8fb8cc', fontSize: 11 }}>
                  📷 No camera rig — the live camera won't respond.
                </span>
                <button
                  type="button"
                  onClick={() => addNodeAt('rigOrbit', { x: 80, y: 80 })}
                  data-testid="add-orbit-rig-editor"
                  style={{
                    cursor: 'pointer', background: '#123240', border: `1px solid ${CAMERA_ACCENT}`,
                    borderRadius: 4, padding: '4px 12px', fontFamily: 'var(--m-font)', fontSize: 11,
                    fontWeight: 'bold', color: '#8fe0ff',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1a4658' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#123240' }}
                >
                  ＋ Add Orbit Camera Rig
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Collection editor modal — rendered at EventGraph level so it has access to variables */}
      {editingVarId && (() => {
        const v = variables.find(x => x.id === editingVarId)
        if (!v) return null
        return (
          <CollectionEditorModal
            variable={v}
            onSave={newValue => {
              onVariablesChange(variables.map(x => x.id === editingVarId ? { ...x, value: newValue } : x))
              setEditingVarId(null)
            }}
            onClose={() => setEditingVarId(null)}
          />
        )
      })()}
    </div>
  )
}

// ─── FunctionEditor ───────────────────────────────────────────────────────────
function FunctionEditor({ fnDef, functions, functionDefs, nodeLastFired, onFnDefChange, variables = [] }) {
  const [fnNodes, setFnNodes, onFnNodesChange] = useNodesState(fnDef.nodes ?? [])
  const [fnEdges, setFnEdges, onFnEdgesChange] = useEdgesState(fnDef.edges ?? [])
  const focusRef = useRef(() => {})

  // Sync inner graph changes back to function def
  useEffect(() => {
    onFnDefChange({ nodes: fnNodes, edges: fnEdges })
  }, [fnNodes, fnEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNodeData = useCallback((nodeId, key, value) => {
    setFnNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setFnNodes])

  const nodesWithHandlers = useStableNodeData(fnNodes, n => ({
    onChange:        (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired: nodeLastFired[n.id] ?? 0,
    functions,
    fnDef,
  }), [nodeLastFired, functions, fnDef, updateNodeData])

  const onConnect = useCallback((params) => {
    setFnEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setFnEdges])

  // ── Undo history (ENG-878) — scoped to this drill-in; the editor unmounts
  // when the view closes, so the keydown listener scopes itself. ──
  const history = useRef(createGraphHistory()).current
  const fnNodesRef = useRef(fnNodes); fnNodesRef.current = fnNodes
  const fnEdgesRef = useRef(fnEdges); fnEdgesRef.current = fnEdges
  const onBeforeDelete = useCallback(async () => {
    history.push(fnNodesRef.current, fnEdgesRef.current)
    return true
  }, [history])
  const clipboardHost = useEventClipboardHost({
    setNodes: setFnNodes, setEdges: setFnEdges, history,
    nodesRef: fnNodesRef, edgesRef: fnEdgesRef,
    variables, functions,
  })
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(fnNodesRef.current, fnEdgesRef.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setFnNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setFnNodes])
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setFnNodes(snap.nodes)
      setFnEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setFnNodes, setFnEdges])

  const addNodeAt = useCallback((type, position, functionId) => {
    let data = {}
    if (type === 'print' || type === 'printString') data = { duration: 5, color: '#ffff00' }
    if (type === 'delay')          data = { duration: 1 }
    if (type === 'branch')         data = {}
    if (type === 'setVariable')    data = { varName: '', varValue: '' }
    if (type === 'random')         data = { randomType: 'float', min: 0, max: 1 }
    if (type === 'select')         data = { selectType: 'string', optionCount: 2, index: 0 }
    if (type === 'concatString')   data = { count: 2 }
    if (type === 'makeLiteralString') data = { value: '' }
    if (type === 'makeLiteralFloat')  data = { value: 0.0 }
    if (type === 'makeLiteralInt')    data = { value: 0 }
    if (type === 'getVariable')    data = { varName: '' }
    if (type === 'functionCall')   data = { functionId: functionId ?? '' }
    if (type === 'functionReturn') data = {}
    setFnNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [setFnNodes])

  const deleteNode = useCallback((id) => {
    // Don't allow deleting the locked entry node
    if (id === 'entry') return
    history.push(fnNodesRef.current, fnEdgesRef.current)
    setFnNodes(ns => ns.filter(n => n.id !== id))
    setFnEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setFnNodes, setFnEdges, history])

  const duplicateNode = useCallback((id) => {
    if (id === 'entry') return
    setFnNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, functions: _f, fnDef: _d, ...cleanData } = node.data
      return [...ns, { ...node, id: nextEvId(), position: { x: node.position.x + 40, y: node.position.y + 40 }, data: cleanData, selected: false }]
    })
  }, [setFnNodes])

  // ── Signature editor helpers ──
  const addInput = () => {
    const name = `input${(fnDef.inputs?.length ?? 0) + 1}`
    onFnDefChange({ inputs: [...(fnDef.inputs ?? []), { name, type: 'string' }] })
  }
  const removeInput = (idx) => {
    onFnDefChange({ inputs: fnDef.inputs.filter((_, i) => i !== idx) })
  }
  const updateInput = (idx, patch) => {
    onFnDefChange({ inputs: fnDef.inputs.map((p, i) => i === idx ? { ...p, ...patch } : p) })
  }
  const addOutput = () => {
    const name = `output${(fnDef.outputs?.length ?? 0) + 1}`
    onFnDefChange({ outputs: [...(fnDef.outputs ?? []), { name, type: 'string' }] })
  }
  const removeOutput = (idx) => {
    onFnDefChange({ outputs: fnDef.outputs.filter((_, i) => i !== idx) })
  }
  const updateOutput = (idx, patch) => {
    onFnDefChange({ outputs: fnDef.outputs.map((p, i) => i === idx ? { ...p, ...patch } : p) })
  }

  const pinTypes = ['string', 'float', 'int', 'bool']
  const PIN_COLOR = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8' }
  const inputSty = {
    background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
    color: 'var(--m-fg-2)', borderRadius: 3, padding: '2px 5px',
    fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Signature editor panel */}
      <div style={{
        background: 'var(--m-panel)', borderBottom: '1px solid var(--m-line)',
        padding: '8px 12px', display: 'flex', gap: 24, flexShrink: 0, flexWrap: 'wrap',
      }}>
        {/* Inputs */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>INPUTS</div>
          {(fnDef.inputs ?? []).map((pin, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_COLOR[pin.type] ?? '#888', flexShrink: 0 }} />
              <input
                value={pin.name}
                onChange={e => updateInput(i, { name: e.target.value })}
                style={{ ...inputSty, width: 90 }}
              />
              <select
                value={pin.type}
                onChange={e => updateInput(i, { type: e.target.value })}
                style={{ ...inputSty, color: PIN_COLOR[pin.type] ?? '#aaa', cursor: 'pointer' }}
              >
                {pinTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span
                onClick={() => removeInput(i)}
                style={{ color: '#301820', cursor: 'pointer', fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.color = '#c06060'}
                onMouseLeave={e => e.currentTarget.style.color = '#301820'}
              >✕</span>
            </div>
          ))}
          <button onClick={addInput} style={{ background: 'none', border: '1px solid var(--m-line)', color: '#2a4060', borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer', marginTop: 2 }}>
            + input
          </button>
        </div>

        {/* Outputs */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>OUTPUTS</div>
          {(fnDef.outputs ?? []).map((pin, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_COLOR[pin.type] ?? '#888', flexShrink: 0 }} />
              <input
                value={pin.name}
                onChange={e => updateOutput(i, { name: e.target.value })}
                style={{ ...inputSty, width: 90 }}
              />
              <select
                value={pin.type}
                onChange={e => updateOutput(i, { type: e.target.value })}
                style={{ ...inputSty, color: PIN_COLOR[pin.type] ?? '#aaa', cursor: 'pointer' }}
              >
                {pinTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span
                onClick={() => removeOutput(i)}
                style={{ color: '#301820', cursor: 'pointer', fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.color = '#c06060'}
                onMouseLeave={e => e.currentTarget.style.color = '#301820'}
              >✕</span>
            </div>
          ))}
          <button onClick={addOutput} style={{ background: 'none', border: '1px solid var(--m-line)', color: '#2a4060', borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer', marginTop: 2 }}>
            + output
          </button>
        </div>

        {/* Rename */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>NAME</div>
          <input
            value={fnDef.name}
            onChange={e => onFnDefChange({ name: e.target.value })}
            style={{ ...inputSty, width: 140 }}
          />
        </div>
      </div>

      {/* Inner graph canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlowProvider>
          <EventCanvas
            nodesWithHandlers={nodesWithHandlers}
            edges={fnEdges}
            onNodesChange={onFnNodesChange}
            onEdgesChange={onFnEdgesChange}
            onConnect={onConnect}
            onBeforeDelete={onBeforeDelete}
            onArrange={onArrange}
            addNodeAt={addNodeAt}
            focusRef={focusRef}
            onDeleteNode={deleteNode}
            onDuplicateNode={duplicateNode}
            clipboardHost={clipboardHost}
            isFunctionEditor={true}
            functionDefs={functionDefs}
            focusContainerId={fnDef.id ?? null}
          />
        </ReactFlowProvider>
      </div>
    </div>
  )
}

// ─── GroupEditor ────────────────────────────────────────────────────────────
// Drill-in editor for a `graphGroup` node's inner graph (camera-in-blueprint Phase 3).
// GENERALIZES FunctionEditor's canvas-swap machinery — same useNodesState/useEdgesState +
// EventCanvas wiring, same sync-on-change pattern — rather than being a parallel editor. It
// differs from FunctionEditor in exactly the ways a group differs from a function:
//   - no signature panel (a group carries no exec/data pins in v1);
//   - storage is inline on the group's OWN node (data.innerNodes/innerEdges), not a top-level
//     `functions` dict keyed by id, so sync-back goes through `onGroupNodeChange` (a patch
//     applied to that one node) rather than `onFnDefChange`;
//   - the palette is the FULL top-level catalog (events included) minus 'graphGroup' itself and
//     the rig-template entries (both would nest a group — see ContextMenu's isGroupEditor
//     filter) — a function editor additionally bans ALL event nodes, a group does not.
function GroupEditor({ groupNode, functions, functionDefs, nodeLastFired, onGroupNodeChange, topNodes = [], variables = [], openFunction }) {
  const [grpNodes, setGrpNodes, onGrpNodesChange] = useNodesState(groupNode.data?.innerNodes ?? [])
  const [grpEdges, setGrpEdges, onGrpEdgesChange] = useEdgesState(groupNode.data?.innerEdges ?? [])
  const focusRef = useRef(() => {})

  // Sync inner graph changes back to the group node's inline data.
  useEffect(() => {
    onGroupNodeChange({ innerNodes: grpNodes, innerEdges: grpEdges })
  }, [grpNodes, grpEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNodeData = useCallback((nodeId, key, value) => {
    setGrpNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setGrpNodes])

  const nodesWithHandlers = useStableNodeData(grpNodes, n => ({
    onChange:         (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired:  nodeLastFired[n.id] ?? 0,
    functions,
    variables,
    onOpenFunction:   n.type === 'functionCall' && openFunction ? () => openFunction(n.data.functionId) : undefined,
  }), [nodeLastFired, functions, variables, openFunction, updateNodeData])

  const onConnect = useCallback((params) => {
    setGrpEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setGrpEdges])

  // ── Undo history (ENG-878) — scoped to this drill-in; the editor unmounts
  // when the view closes, so the keydown listener scopes itself. ──
  const history = useRef(createGraphHistory()).current
  const grpNodesRef = useRef(grpNodes); grpNodesRef.current = grpNodes
  const grpEdgesRef = useRef(grpEdges); grpEdgesRef.current = grpEdges
  const onBeforeDelete = useCallback(async () => {
    history.push(grpNodesRef.current, grpEdgesRef.current)
    return true
  }, [history])
  const clipboardHost = useEventClipboardHost({
    setNodes: setGrpNodes, setEdges: setGrpEdges, history,
    nodesRef: grpNodesRef, edgesRef: grpEdgesRef,
    variables, functions,
  })
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(grpNodesRef.current, grpEdgesRef.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setGrpNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setGrpNodes])
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setGrpNodes(snap.nodes)
      setGrpEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setGrpNodes, setGrpEdges])

  const addNodeAt = useCallback((type, position, functionId) => {
    // Event-category uniqueness (e.g. one beginPlay) is scoped to THIS group's own inner graph,
    // never the top-level graph or a sibling group — each group needs its own beginPlay to be
    // reachable at all (EventRuntime dispatches beginPlay against every group's pool
    // independently). GLOBAL_UNIQUE_TYPES (voxyConfig/convorcherSession) are the exception: those
    // must not already exist at the top level or inside any sibling group either, or the runtime
    // double-fires them (double mic init / double session declare).
    const palEntry = EVENT_PALETTE.find(p => p.type === type)
    if (palEntry?.unique) {
      const localDup = grpNodes.some(n => n.type === type)
      const globalDup = GLOBAL_UNIQUE_TYPES.has(type) &&
        globalUniqueTypesInUse(topNodes, groupNode.id).has(type)
      if (localDup || globalDup) return
    }
    // The palette (isGroupEditor filter) already excludes 'graphGroup' and rig templates, so
    // no cluster/nested-group special-casing is needed here — every addable type is a plain node.
    const data = defaultNodeData(type, functionId)
    setGrpNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [grpNodes, setGrpNodes, topNodes, groupNode.id])

  const deleteNode = useCallback((id) => {
    history.push(grpNodesRef.current, grpEdgesRef.current)
    setGrpNodes(ns => ns.filter(n => n.id !== id))
    setGrpEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setGrpNodes, setGrpEdges, history])

  const duplicateNode = useCallback((id) => {
    setGrpNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, functions: _f, ...cleanData } = node.data
      return [...ns, { ...node, id: nextEvId(), position: { x: node.position.x + 40, y: node.position.y + 40 }, data: cleanData, selected: false }]
    })
  }, [setGrpNodes])

  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <ReactFlowProvider>
        <EventCanvas
          nodesWithHandlers={nodesWithHandlers}
          edges={grpEdges}
          onNodesChange={onGrpNodesChange}
          onEdgesChange={onGrpEdgesChange}
          onConnect={onConnect}
          onBeforeDelete={onBeforeDelete}
          onArrange={onArrange}
          addNodeAt={addNodeAt}
          focusRef={focusRef}
          onDeleteNode={deleteNode}
          onDuplicateNode={duplicateNode}
          clipboardHost={clipboardHost}
          isGroupEditor={true}
          functionDefs={functionDefs}
          extraExistingTypes={globalUniqueTypesInUse(topNodes, groupNode.id)}
          focusContainerId={groupNode.id}
        />
      </ReactFlowProvider>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FunctionSidebarRow({ fn, active, onOpen, onDelete }) {
  const [menu, setMenu] = useState(null)

  const handleContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY })
  }

  const closeMenu = () => setMenu(null)

  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px 4px 12px', cursor: 'pointer',
          borderBottom: '1px solid var(--m-line)',
          background: active ? 'var(--m-panel-hi)' : 'transparent',
        }}
        onClick={onOpen}
        onContextMenu={handleContextMenu}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--m-panel-hi)' }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
      >
        <span style={{ color: '#7040c0', fontSize: 11 }}>⬡</span>
        <span style={{ color: '#9060d0', fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fn.name}
        </span>
      </div>

      {menu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={closeMenu} onContextMenu={closeMenu} />
          <AnchoredOverlay
            x={menu.x} y={menu.y}
            style={{
              background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
              borderRadius: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
              zIndex: 1000, fontFamily: 'var(--m-font)', fontSize: 11, overflow: 'hidden',
              minWidth: 130,
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div
              onClick={() => { onOpen(); closeMenu() }}
              style={{ padding: '7px 12px', cursor: 'pointer', color: 'var(--m-fg-2)', borderBottom: '1px solid var(--m-line)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--m-panel-hi)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >Open</div>
            <div
              onClick={() => { onDelete(); closeMenu() }}
              style={{ padding: '7px 12px', cursor: 'pointer', color: '#e06060' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--m-panel-hi)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >Delete</div>
          </AnchoredOverlay>
        </>
      )}
    </>
  )
}

function Section({ label, children, action }) {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ borderBottom: '1px solid var(--m-line)' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px',
          background: 'var(--m-panel)',
          color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1.2, fontWeight: 'bold',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 8, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>{open ? '▼' : '▶'}</span>
        <span style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>{label}</span>
        {action && (
          <span
            title={action.title}
            onClick={e => { e.stopPropagation(); action.onClick() }}
            style={{ color: '#2a4060', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}
            onMouseEnter={e => e.currentTarget.style.color = '#4a80c0'}
            onMouseLeave={e => e.currentTarget.style.color = '#2a4060'}
          >{action.label}</span>
        )}
      </div>
      {open && children}
    </div>
  )
}

function coerceToType(raw, type) {
  if (type === 'bool')  return raw === 'true' || raw === true
  if (type === 'int')   return Math.round(Number(raw)) || 0
  if (type === 'float') return Number(raw) || 0
  return String(raw ?? '')
}

function CollectionEditorModal({ variable, onSave, onClose }) {
  const isArray    = variable.type === 'array'
  const itemType   = variable.itemType  ?? 'string'
  const keyType    = variable.keyType   ?? 'string'
  const valueType  = variable.valueType ?? 'string'
  const accent     = TYPE_COLOR[variable.type] ?? '#888'

  const [items, setItems] = useState(() =>
    isArray
      ? (Array.isArray(variable.value) ? [...variable.value] : [])
      : Object.entries(variable.value ?? {}).map(([k, v]) => ({ k, v: String(v) }))
  )
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState(valueType === 'bool' || itemType === 'bool' ? 'false' : '')

  const effectiveValType = isArray ? itemType : valueType

  const addItem = () => {
    if (isArray) {
      setItems(prev => [...prev, coerceToType(newVal, itemType)])
      setNewVal(itemType === 'bool' ? 'false' : '')
    } else {
      const k = keyType === 'int' ? String(Math.round(Number(newKey)) || 0) : newKey.trim()
      if (!k) return
      setItems(prev => [...prev.filter(i => i.k !== k), { k, v: newVal }])
      setNewKey('')
      setNewVal(valueType === 'bool' ? 'false' : '')
    }
  }

  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx))

  const updateItemVal = (idx, raw) => setItems(prev => prev.map((x, i) => {
    if (i !== idx) return x
    return isArray ? coerceToType(raw, itemType) : { ...x, v: raw }
  }))

  const save = () => {
    if (isArray) {
      onSave(items)
    } else {
      const map = {}
      for (const { k, v } of items) map[k] = coerceToType(v, valueType)
      onSave(map)
    }
  }

  const inputSty = (w) => ({
    width: w, background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
    color: 'var(--m-fg-2)', borderRadius: 3, padding: '2px 5px',
    fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
  })
  const btnSty = (color) => ({
    background: 'none', border: `1px solid ${color}50`, color,
    borderRadius: 3, padding: '2px 8px', fontSize: 10,
    fontFamily: 'var(--m-font)', cursor: 'pointer',
  })

  const title = isArray
    ? `${variable.name}  :  array<${itemType}>`
    : `${variable.name}  :  map<${keyType}, ${valueType}>`

  // Called as a plain function, NOT as <ValInput/>, to avoid remount-on-render
  const renderValInput = (value, onChange, width) =>
    effectiveValType === 'bool' ? (
      <select value={String(value)} onChange={e => onChange(e.target.value)}
        style={{ ...inputSty(width ?? 70), cursor: 'pointer' }}>
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    ) : (
      <input
        type={effectiveValType === 'int' || effectiveValType === 'float' ? 'number' : 'text'}
        step={effectiveValType === 'float' ? 0.1 : 1}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && addItem()}
        style={inputSty(width ?? (isArray ? 200 : 130))}
      />
    )

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{ background: 'var(--m-panel)', border: `1px solid ${accent}40`, borderRadius: 6, padding: 16, minWidth: 340, maxWidth: 480, boxShadow: `0 0 24px ${accent}18`, fontFamily: 'var(--m-font)' }}
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ color: accent, fontSize: 11, fontWeight: 'bold', flex: 1 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--m-fg-3)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>

        {/* Items list */}
        <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.length === 0 && <div style={{ color: 'var(--m-fg-3)', fontSize: 10, padding: '4px 0' }}>empty</div>}
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9, width: isArray ? 18 : 'auto', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {isArray ? i : item.k}
              </span>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>{isArray ? '→' : ':'}</span>
              {renderValInput(isArray ? String(item) : item.v, raw => updateItemVal(i, raw), isArray ? 220 : 160)}
              <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: '#4a2020', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>

        {/* Add row */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderTop: '1px solid var(--m-line)', paddingTop: 10, marginBottom: 14 }}>
          {!isArray && (
            <>
              <input
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()}
                placeholder={keyType === 'int' ? '0' : 'key'}
                type={keyType === 'int' ? 'number' : 'text'}
                style={inputSty(keyType === 'int' ? 55 : 80)}
              />
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>:</span>
            </>
          )}
          {renderValInput(newVal, setNewVal)}
          <button onClick={addItem} style={btnSty(accent)}>+ Add</button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button onClick={onClose} style={btnSty('var(--m-fg-3)')}>Cancel</button>
          <button onClick={save} style={btnSty(accent)}>Save</button>
        </div>
      </div>
    </div>
  )
}

function VariableRow({ variable, onNameChange, onValueChange, onRemove, onEdit }) {
  const typeColor = TYPE_COLOR[variable.type] ?? '#888'
  const isCollection = variable.type === 'array' || variable.type === 'map'

  // Human-readable type signature e.g. "array<string>" or "map<int,float>"
  const typeLabel = variable.type === 'array'
    ? `array<${variable.itemType ?? '?'}>`
    : variable.type === 'map'
      ? `map<${variable.keyType ?? '?'},${variable.valueType ?? '?'}>`
      : variable.type

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: '4px 10px', gap: 5,
      borderBottom: '1px solid var(--m-line)',
    }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: typeColor, flexShrink: 0 }} />
      <input
        value={variable.name}
        onChange={e => onNameChange(e.target.value)}
        style={{
          flex: 1, background: 'transparent', border: 'none',
          borderBottom: '1px solid var(--m-line)', color: 'var(--m-fg-2)',
          fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', padding: '1px 0',
        }}
      />
      {isCollection ? (
        <>
          <span style={{
            color: `${typeColor}66`, fontSize: 9, fontFamily: 'var(--m-font)',
            background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}20`,
            borderRadius: 2, padding: '1px 5px', whiteSpace: 'nowrap',
          }}>
            {variable.type === 'array'
              ? `[${Array.isArray(variable.value) ? variable.value.length : 0}]`
              : `{${variable.value && typeof variable.value === 'object' ? Object.keys(variable.value).length : 0}}`
            }
          </span>
          <button
            onClick={onEdit}
            title="Edit items"
            style={{
              background: 'none', border: `1px solid ${typeColor}30`, color: typeColor,
              borderRadius: 3, padding: '1px 5px', fontSize: 9,
              fontFamily: 'var(--m-font)', cursor: 'pointer', lineHeight: 1,
            }}
          >
            +
          </button>
        </>
      ) : variable.type === 'bool' ? (
        <select
          value={String(variable.value)}
          onChange={e => onValueChange(e.target.value)}
          style={{
            background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px', fontSize: 9, cursor: 'pointer', width: 46,
          }}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : variable.type === 'string' ? (
        <input
          type="text"
          value={variable.value ?? ''}
          onChange={e => onValueChange(e.target.value)}
          style={{
            width: 60, background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px',
            fontSize: 9, fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
      ) : (
        <input
          type="number"
          step={variable.type === 'float' ? 0.1 : 1}
          value={variable.value}
          onChange={e => onValueChange(e.target.value)}
          style={{
            width: 46, background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px',
            fontSize: 9, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
          }}
        />
      )}
      <span style={{ color: typeColor, fontSize: 8, maxWidth: 80, textAlign: 'right', opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {typeLabel}
      </span>
      <button
        onClick={onRemove}
        style={{ background: 'none', border: 'none', color: '#3a2020', cursor: 'pointer', fontSize: 12, padding: '0 1px', lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  )
}
